import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  DateRangePicker,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormMessage,
  InfoHint,
  Input,
  PaginationControls,
  Progress,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
  Textarea,
} from '@shared/ui';
import {
  buildCronFromInterval,
  MINUTES_INTERVAL_VALUES,
  parseCronInterval,
  type SyncWorkerMinutesInterval,
} from '@shared/lib/sync-worker-settings/cron-interval';
import {
  DERIVED_POSTPROCESS_FIELDS,
  INTERNAL_DATA_VALIDATION_FIELDS,
  INTERNAL_DELETE_LANE_SAFEGUARD_FIELDS,
  INTERNAL_MEDIA_TUNING_FIELDS,
  INTERNAL_RELATION_REFRESH_SAFEGUARD_FIELDS,
  INTERNAL_SNAPSHOT_LANE_SAFEGUARD_FIELDS,
  LEGACY_MIRROR_DELTA_FIELDS,
  RELATION_DEAD_REPAIR_BATCH_SIZE_MAX,
  RELATION_REFRESH_BULK_MICROBATCH_SIZE_MAX,
} from '@features/admin-sync-worker-settings';
import type {
  SyncWorkerSettingsMeta,
  SyncWorkerSlackReport,
  SyncWorkerSettingsUpdatePayload,
} from '@features/admin-sync-worker-settings';
import type {
  CreateSyncReconciliationRunInput,
  SyncReconciliationRunMode,
} from '@features/admin-sync-reconciliation';
import type {
  StartSyncDataValidationRunInput,
  SyncDataValidationRun,
  SyncDataValidationRunsPagination,
} from '@features/admin-sync-data-validation';

const WEBHOOK_INBOX_BULK_ACK_MIN_RECORDS_DEFAULT = 25;
const WEBHOOK_INBOX_BULK_ACK_MIN_RECORDS_MAX = 250;

type SyncWorkerSettingsFormValues = {
  workerRuntimeEnabled: boolean;
  apiEnqueueEnabled: boolean;
  syncPipelineEnabled: boolean;
  deleteExecutionEnabled: boolean;
  deleteBacklogCron: string;
  deleteBacklogLeaseMs: number;
  deleteBacklogMaxAttempts: number;
  deleteBacklogBaseBackoffSeconds: number;
  deleteBacklogMaxBackoffSeconds: number;
  deleteVerificationTimeoutMs: number;
  deleteBacklogMaxRowsPerRun: number;
  deleteBacklogClaimBatchSize: number;
  deleteBacklogMaxDurationMs: number;
  deleteWebhookInboxMaxRecordsPerRun: number;
  deleteWebhookInboxMaxIdsPerRecord: number;
  createUpdateWebhookInboxMaxRecordsPerRun: number;
  deleteWebhookInboxBulkAckMinRecords: number;
  createUpdateWebhookInboxBulkAckMinRecords: number;
  snapshotExecutionEnabled: boolean;
  snapshotSupervisorIntervalSeconds: number;
  snapshotJobsFreshnessSlaSeconds: number;
  snapshotDataTypesFreshnessSlaSeconds: number;
  snapshotRelationQueueFreshnessSlaSeconds: number;
  snapshotLeaseWindowSeconds: number;
  snapshotHeartbeatIntervalSeconds: number;
  globalDeltaCron: string;
  inventoryPostgresCountEnabled: boolean;
  inventoryPostgresCountCron: string;
  inventoryBubbleCountEnabled: boolean;
  inventoryBubbleCountCron: string;
  analyticsGearEnabled: boolean;
  inventoryCountsStaleWarningThresholdMinutes: number;
  syncMatchFreshnessHours: number;
  columnParityRefreshEnabled: boolean;
  columnParityRefreshCron: string;
  columnParityRefreshMaxAttempts: number;
  columnParityRefreshBaseBackoffSeconds: number;
  columnParityRefreshMaxBackoffSeconds: number;
  dataValidationEnabled: boolean;
  dataValidationScheduleEnabled: boolean;
  dataValidationScheduleCron: string;
  dataValidationLookbackDays: number;
  dataValidationSafetyLagDays: number;
  dataValidationRepairWaitMinutes: number;
  dataValidationMaxRowsPerTable: number;
  dataValidationDirtyRecheckAttempts: number;
  dataValidationMaxTablesPerCampaign: number;
  dataValidationMaxSampleLimit: number;
  dataValidationDefaultChunkSize: number;
  dataValidationBubbleRequestConcurrency: number;
  dataValidationBubbleRequestsPerMinute: number;
  dataValidationReportRetentionDays: number;
  dataValidationFullScanEnabled: boolean;
  dataValidationLeaseMs: number;
  dataValidationCleanupBatchSize: number;
  dataValidationMaxReportBytes: number;
  dataValidationSnapshotRetentionDays: number;
  dataValidationDirtySnapshotRetentionDays: number;
  dataValidationRawReportRetentionDays: number;
  dataValidationFinalReportRetentionDays: number;
  dataValidationFreshnessDays: number;
  refreshBatchSize: number;
  refreshBatchDelayMs: number;
  relationRefreshCron: string;
  relationRefreshBatchLimit: number;
  relationRefreshLeaseMs: number;
  relationRefreshLockTimeoutMs: number;
  relationRefreshStatementTimeoutMs: number;
  relationRefreshIdleInTxTimeoutMs: number;
  relationRefreshEnabled: boolean;
  relationRefreshBulkEnabled: boolean;
  relationRefreshBulkCron: string;
  relationRefreshBulkMicrobatchSize: number;
  relationRefreshBulkTimeBudgetMs: number;
  relationRefreshBulkLeaseMs: number;
  relationSnapshotExactRecountEnabled: boolean;
  relationSnapshotExactRecountCron: string;
  relationSnapshotExactRecountStatementTimeoutMs: number;
  relationSnapshotExactRecountMaxTablesPerRun: number;
  exactIdSyncEnabled: boolean;
  exactIdTerminalRetentionDays: number;
  exactIdNotFoundClassifierCron: string;
  exactIdNotFoundClassifierSafetyLagMinutes: number;
  retentionCleanupIntervalMinutes: number;
  syncHistoryCleanupBatchSize: number;
  workerIncidentsCleanupBatchSize: number;
  syncTableQueueCleanupBatchSize: number;
  syncExactIdQueueCleanupBatchSize: number;
  jobRunsCompletedCleanupBatchSize: number;
  jobRunsFailedCancelledCleanupBatchSize: number;
  syncCountAuditLeaseCleanupBatchSize: number;
  relationDoneRowsCleanupBatchSize: number;
  deletedRelationCleanupQueueBatchSize: number;
  syncReconciliationRunsCleanupBatchSize: number;
  syncEntityReviewBacklogCleanupBatchSize: number;
  bubbleMutationSuccessCleanupBatchSize: number;
  bubbleMutationNonSuccessRedactionBatchSize: number;
  webhookInboxLedgerCleanupBatchSize: number;
  syncTableQueueTotalExecutionsLimit: number;
  syncExactIdQueueTotalExecutionsLimit: number;
  relationRefreshQueueTotalExecutionsLimit: number;
  mediaOwnerSyncQueueTotalExecutionsLimit: number;
  mediaImportQueueTotalExecutionsLimit: number;
  webhookDeltaFallbackEnabled: boolean;
  relationDeadRepairEnabled: boolean;
  relationDeadRepairBatchSize: number;
  relationDeadRepairTimeBudgetMs: number;
  relationDeadRepairBubbleRateLimitPerMinute: number;
  relationDeadRepairLeaseMs: number;
  relationDeadRepairMaxAttempts: number;
  relationDeadRepairRetentionDays: number;
  relationDeadRepairRootCron: string;
  relationDeadRepairContinuationDelayMs: number;
  deletedRelationCleanupEnabled: boolean;
  deletedRelationCleanupBatchSize: number;
  deletedRelationCleanupRateLimitPerMinute: number;
  deletedRelationCleanupLeaseMs: number;
  deletedRelationCleanupMaxAttempts: number;
  deletedRelationCleanupRetentionDays: number;
  syncReconciliationMaxIdsPerBubbleRecheckBatch: number;
  syncReconciliationMaxConflictRecheckBatchSize: number;
  syncReconciliationMaxPagesPerBucket: number;
  syncReconciliationMaxBucketsPerRun: number;
  syncReconciliationMaxActionsPerRun: number;
  syncReconciliationMaxDeleteActionsPerRun: number;
  syncReconciliationMaxExactIdActionsPerRun: number;
  syncReconciliationMaxDurationMs: number;
  syncReconciliationSafetyLagMinutes: number;
  syncReconciliationBucketMaxAttempts: number;
  syncReconciliationActionRetentionDays: number;
  syncReconciliationTierMaxRunsPerTick: number;
  syncReconciliationTierDefaultLookbackHours: number;
  syncSymmetricReconciliationEnabled: boolean;
  countMismatchDelayedRecountDelayMinutes: number;
  mirrorDeltaPageSize: number;
  mirrorDeltaBatchSize: number;
  mirrorDeltaWorkBatchSize: number;
  mirrorDeltaPollIntervalSeconds: number;
  maxActiveSyncTables: number;
  exactIdMaxActiveTables: number;
  deltaMaxActiveTables: number;
  standardMainChunkSize: number;
  heavyRelationMainChunkSize: number;
  standardPostprocessMicrobatchSize: number;
  heavyRelationPostprocessMicrobatchSize: number;
  postprocessLeaseMs: number;
  postprocessTimeBudgetMs: number;
  mediaImportSmallConcurrency: number;
  mediaImportSchedulerEnabled: boolean;
  mediaOwnerSyncSchedulerEnabled: boolean;
  mediaOwnerMissingReconcileIntervalMinutes: number;
  mediaOwnerSyncClaimBatchSize: number;
  mediaOwnerSyncProcessConcurrency: number;
  mediaOwnerSyncMaxActive: number;
  mediaOwnerSyncTickTimeBudgetMs: number;
  mediaOwnerSyncDbPressureBreakerEnabled: boolean;
  mediaOwnerSyncClaimDbPressureThresholdMs: number;
  testModeEnabled: boolean;
  testModeBubbleKey: string | null;
  testModeRecordLimit: number;
};

const normalizeSyncWorkerSettingsFormValues = (
  settings: SyncWorkerSettingsFormValues
): SyncWorkerSettingsFormValues => ({
  ...settings,
  relationRefreshBulkMicrobatchSize: Math.min(
    settings.relationRefreshBulkMicrobatchSize,
    RELATION_REFRESH_BULK_MICROBATCH_SIZE_MAX
  ),
  relationDeadRepairBatchSize: Math.max(
    1,
    Math.min(settings.relationDeadRepairBatchSize, RELATION_DEAD_REPAIR_BATCH_SIZE_MAX)
  ),
});

const OPERATOR_HIDDEN_SETTING_FIELDS = new Set<string>([
  ...INTERNAL_MEDIA_TUNING_FIELDS,
  ...LEGACY_MIRROR_DELTA_FIELDS,
  ...DERIVED_POSTPROCESS_FIELDS,
  ...INTERNAL_DELETE_LANE_SAFEGUARD_FIELDS,
  'deleteBacklogClaimBatchSize',
  'deleteBacklogMaxDurationMs',
  'deletedRelationCleanupLeaseMs',
  'deletedRelationCleanupMaxAttempts',
  'deletedRelationCleanupRetentionDays',
  ...INTERNAL_RELATION_REFRESH_SAFEGUARD_FIELDS,
  ...INTERNAL_SNAPSHOT_LANE_SAFEGUARD_FIELDS,
  ...INTERNAL_DATA_VALIDATION_FIELDS,
  'syncReconciliationMaxIdsPerBubbleRecheckBatch',
  'syncReconciliationMaxConflictRecheckBatchSize',
  'syncReconciliationMaxPagesPerBucket',
  'syncReconciliationMaxBucketsPerRun',
  'syncReconciliationMaxActionsPerRun',
  'syncReconciliationMaxDeleteActionsPerRun',
  'syncReconciliationMaxExactIdActionsPerRun',
  'syncReconciliationMaxDurationMs',
  'syncReconciliationSafetyLagMinutes',
  'syncReconciliationBucketMaxAttempts',
  'syncReconciliationActionRetentionDays',
  'syncReconciliationTierMaxRunsPerTick',
  'syncReconciliationTierDefaultLookbackHours',
]);

const MAINTENANCE_CLEANUP_BATCH_FIELDS = [
  {
    field: 'syncHistoryCleanupBatchSize',
    label: 'Sync history',
    help: 'Successful technical sync-history rows deleted in one maintenance pass.',
  },
  {
    field: 'workerIncidentsCleanupBatchSize',
    label: 'Worker incidents',
    help: 'Resolved or obsolete worker incidents deleted in one maintenance pass.',
  },
  {
    field: 'syncTableQueueCleanupBatchSize',
    label: 'Sync table queue',
    help: 'Completed or failed sync-table queue rows deleted in one maintenance pass.',
  },
  {
    field: 'syncExactIdQueueCleanupBatchSize',
    label: 'Exact-ID queue',
    help: 'Terminal exact-ID queue rows deleted in one maintenance pass.',
  },
  {
    field: 'jobRunsCompletedCleanupBatchSize',
    label: 'Completed job runs',
    help: 'Completed job-run history deleted in one maintenance pass.',
  },
  {
    field: 'jobRunsFailedCancelledCleanupBatchSize',
    label: 'Failed and cancelled job runs',
    help: 'Failed or cancelled job-run history deleted in one maintenance pass.',
  },
  {
    field: 'syncCountAuditLeaseCleanupBatchSize',
    label: 'Count-audit leases',
    help: 'Stale count-audit leases terminalized in one maintenance pass.',
  },
  {
    field: 'relationDoneRowsCleanupBatchSize',
    label: 'Completed relation rows',
    help: 'Completed relation-refresh queue rows deleted in one maintenance pass.',
  },
  {
    field: 'deletedRelationCleanupQueueBatchSize',
    label: 'Deleted-relation cleanup queue',
    help: 'Completed deleted-relation cleanup rows deleted in one maintenance pass.',
  },
  {
    field: 'syncReconciliationRunsCleanupBatchSize',
    label: 'Reconciliation runs',
    help: 'Terminal reconciliation-run history deleted in one maintenance pass.',
  },
  {
    field: 'syncEntityReviewBacklogCleanupBatchSize',
    label: 'Entity review backlog',
    help: 'Terminal entity-review backlog rows deleted in one maintenance pass.',
  },
  {
    field: 'bubbleMutationSuccessCleanupBatchSize',
    label: 'Successful outbound operations',
    help: 'Eligible seven-day successful journal and outbox history deleted in one maintenance pass.',
  },
  {
    field: 'bubbleMutationNonSuccessRedactionBatchSize',
    label: 'Non-success outbound redaction',
    help: 'Eligible non-success outbound payloads redacted in one maintenance pass.',
  },
  {
    field: 'webhookInboxLedgerCleanupBatchSize',
    label: 'Webhook inbox ledger',
    help: 'Complete seven-day ACKed webhook parent and target groups deleted atomically in one pass.',
  },
] as const satisfies ReadonlyArray<{
  field: keyof SyncWorkerSettingsFormValues;
  label: string;
  help: string;
}>;

type IntervalKind = 'minutes' | 'hours' | 'days' | 'unsupported';

type IntervalState = {
  kind: IntervalKind;
  minutesValue: SyncWorkerMinutesInterval;
  hoursValue: number;
  dayHour?: number;
};

const buildIntervalState = (cron: string): IntervalState => {
  const parsed = parseCronInterval(cron);
  if (!parsed) {
    return {
      kind: 'unsupported',
      minutesValue: 1,
      hoursValue: 1,
      dayHour: 0,
    };
  }
  if (parsed.kind === 'minutes') {
    return {
      kind: 'minutes',
      minutesValue: parsed.value,
      hoursValue: 1,
      dayHour: 0,
    };
  }
  if (parsed.kind === 'hours') {
    return {
      kind: 'hours',
      minutesValue: 1,
      hoursValue: parsed.value,
      dayHour: 0,
    };
  }
  return {
    kind: 'days',
    minutesValue: 1,
    hoursValue: 1,
    dayHour: parsed.hour ?? 0,
  };
};

const resolveIntervalCron = (state: IntervalState): string => {
  if (state.kind === 'minutes') {
    return buildCronFromInterval({ kind: 'minutes', value: state.minutesValue });
  }
  if (state.kind === 'hours') {
    return buildCronFromInterval({ kind: 'hours', value: state.hoursValue });
  }
  return buildCronFromInterval({ kind: 'days', value: 1, hour: state.dayHour });
};

type FieldErrors = Partial<Record<keyof SyncWorkerSettingsFormValues, string>>;
type DeltaTableOption = {
  value: string;
  label: string;
  enabled: boolean;
};

type ReconciliationTableOption = DeltaTableOption & {
  postgresTableName: string;
};

type ValidationScopeBasis = 'bubble_created_at' | 'bubble_modified_at' | 'postgres_synced_at';
type ManualReconciliationWindowMode = 'recent_window' | 'full_table';

const FULL_TABLE_RECONCILIATION_WINDOW_START = '1970-01-01T00:00:00.000Z';
const DAY_MS = 24 * 60 * 60 * 1000;

function buildSafetyLaggedWindowEndDate(safetyLagMinutes: number, now = new Date()): Date {
  const normalizedSafetyLagMinutes =
    Number.isFinite(safetyLagMinutes) && safetyLagMinutes > 0 ? safetyLagMinutes : 0;
  return new Date(now.getTime() - normalizedSafetyLagMinutes * 60_000);
}

function getLatestManualReconciliationDateWindowEndDate(safetyLagMinutes: number): string {
  const latestWindowEnd = buildSafetyLaggedWindowEndDate(safetyLagMinutes);
  const latestExclusiveEndDayStart = Date.UTC(
    latestWindowEnd.getUTCFullYear(),
    latestWindowEnd.getUTCMonth(),
    latestWindowEnd.getUTCDate()
  );
  return toIsoDateOnly(new Date(latestExclusiveEndDayStart - DAY_MS));
}

const validationScopeBasisLabels: Record<ValidationScopeBasis, string> = {
  bubble_created_at: 'Bubble created',
  bubble_modified_at: 'Bubble modified',
  postgres_synced_at: 'Postgres synced',
};

function parseIsoDateOnly(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }
  const date = new Date(`${value}T00:00:00.000Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function toUtcIsoFromDateOnly(value: string): string | null {
  return parseIsoDateOnly(value)?.toISOString() ?? null;
}

function toExclusiveUtcIsoFromDateOnly(value: string): string | null {
  const date = parseIsoDateOnly(value);
  if (date === null) {
    return null;
  }
  const nextDay = new Date(date.getTime() + 24 * 60 * 60 * 1000);
  return nextDay.toISOString();
}

function toIsoDateOnly(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function getDefaultValidationScopeRange(): { from: string; to: string } {
  const today = new Date();
  const todayUtc = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())
  );
  const yesterdayUtc = new Date(todayUtc.getTime() - 24 * 60 * 60 * 1000);
  const yesterday = toIsoDateOnly(yesterdayUtc);
  return { from: yesterday, to: yesterday };
}

function getDefaultManualReconciliationRange(): { from: string; to: string } {
  return getDefaultValidationScopeRange();
}

function getRecentValidationScopeRange(days: number): { from: string; to: string } {
  const today = new Date();
  const todayUtc = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())
  );
  const endUtc = new Date(todayUtc.getTime() - 24 * 60 * 60 * 1000);
  const startUtc = new Date(endUtc.getTime() - Math.max(0, days - 1) * 24 * 60 * 60 * 1000);
  return {
    from: toIsoDateOnly(startUtc),
    to: toIsoDateOnly(endUtc),
  };
}

function buildValidationScopeDateRangeLabel(from: string, to: string, locale: string): string {
  const fromDate = parseIsoDateOnly(from);
  const toDate = parseIsoDateOnly(to);
  if (fromDate === null || toDate === null) {
    return 'Select window';
  }
  const formatter = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    timeZone: 'UTC',
    year: 'numeric',
  });
  if (from === to) {
    return formatter.format(fromDate);
  }
  return `${formatter.format(fromDate)} - ${formatter.format(toDate)}`;
}

function formatValidationScope(run: SyncDataValidationRun): string | null {
  const scope = run.validationScope;
  if (scope === null) {
    return null;
  }
  const basis =
    scope.basis === 'bubble_created_at' ||
    scope.basis === 'bubble_modified_at' ||
    scope.basis === 'postgres_synced_at'
      ? validationScopeBasisLabels[scope.basis]
      : scope.basis;
  return `${scope.label} - ${basis} [${scope.from}, ${scope.to})`;
}

type AdminSyncWorkerSettingsCardProps = {
  environmentLabel: string;
  settings: SyncWorkerSettingsFormValues | null;
  effectiveSettings?: Partial<SyncWorkerSettingsFormValues> | null;
  settingsMeta?: SyncWorkerSettingsMeta | null;
  slackReport?: SyncWorkerSlackReport | null;
  isLoading: boolean;
  isSaving: boolean;
  isSlackReportPending?: boolean;
  isGlobalPausePending?: boolean;
  isGloballyPaused?: boolean;
  isRunDeltaAllPending?: boolean;
  isRunDeltaSinglePending?: boolean;
  deltaTableOptions?: DeltaTableOption[];
  testModeTableOptions?: DeltaTableOption[];
  validationTableOptions?: DeltaTableOption[];
  reconciliationTableOptions?: ReconciliationTableOption[];
  selectedDeltaTable?: string | null;
  selectedValidationTable?: string | null;
  selectedReconciliationTable?: string | null;
  activeValidationRun?: SyncDataValidationRun | null;
  recentValidationRuns?: SyncDataValidationRun[];
  recentValidationRunsPagination?: SyncDataValidationRunsPagination | null;
  recentValidationRunsPage?: number;
  isReadOnly?: boolean;
  onSubmit: (values: SyncWorkerSettingsUpdatePayload) => Promise<void> | void;
  onSendSlackReport?: () => Promise<void> | void;
  onToggleGlobalPause?: (paused: boolean) => Promise<void> | void;
  onRunDeltaAll?: () => Promise<void> | void;
  onRunDeltaSingle?: (bubbleKey: string) => Promise<void> | void;
  onStartReconciliationRun?: (input: CreateSyncReconciliationRunInput) => Promise<void> | void;
  onStartValidationRun?: (input: StartSyncDataValidationRunInput) => Promise<void> | void;
  onCancelValidationRun?: (runId: string) => Promise<void> | void;
  onOpenValidationReport?: (runId: string) => Promise<void> | void;
  onValidationRunsNextPage?: () => void;
  onValidationRunsPreviousPage?: () => void;
  onValidationRunsPageChange?: (page: number) => void;
  onSelectedDeltaTableChange?: (bubbleKey: string) => void;
  onSelectedValidationTableChange?: (bubbleKey: string) => void;
  onSelectedReconciliationTableChange?: (bubbleKey: string) => void;
  serverError?: string | null;
  fieldErrors?: FieldErrors;
  isStartValidationRunPending?: boolean;
  isStartReconciliationRunPending?: boolean;
  isCancelValidationRunPending?: boolean;
  isOpenValidationReportPending?: boolean;
  isValidationRunsFetching?: boolean;
};

function SettingLabel({ label, help }: { label: string; help: string }) {
  return (
    <div className="flex items-center gap-2">
      <Text className="text-sm font-medium text-card-foreground">{label}</Text>
      <InfoHint text={help} />
    </div>
  );
}

function SectionHeading({ title, description }: { title: string; description: string }) {
  return (
    <div>
      <Text className="text-sm font-semibold text-card-foreground">{title}</Text>
      <Text size="sm" color="muted" className="mt-1">
        {description}
      </Text>
    </div>
  );
}

function RuntimeFieldCard({
  title,
  description,
  children,
  className,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-lg border border-border p-4 ${className ?? ''}`}>
      <Stack gap="sm">
        <SectionHeading title={title} description={description} />
        {children}
      </Stack>
    </div>
  );
}

function formatUtcDateTime(value: string, locale: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const resolvedLocale = locale === 'ru' ? 'ru-RU' : 'en-US';

  return `${date.toLocaleString(resolvedLocale, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  })} UTC`;
}

export function AdminSyncWorkerSettingsCard({
  environmentLabel,
  settings,
  effectiveSettings = null,
  settingsMeta = null,
  slackReport = null,
  isLoading,
  isSaving,
  isSlackReportPending = false,
  isGlobalPausePending = false,
  isGloballyPaused = false,
  isRunDeltaAllPending = false,
  isRunDeltaSinglePending = false,
  deltaTableOptions = [],
  testModeTableOptions = [],
  validationTableOptions = [],
  reconciliationTableOptions = [],
  selectedDeltaTable = null,
  selectedValidationTable = null,
  selectedReconciliationTable = null,
  activeValidationRun = null,
  recentValidationRuns = [],
  recentValidationRunsPagination = null,
  recentValidationRunsPage = 1,
  isReadOnly = false,
  onSubmit,
  onSendSlackReport,
  onToggleGlobalPause,
  onRunDeltaAll,
  onRunDeltaSingle,
  onStartReconciliationRun,
  onStartValidationRun,
  onCancelValidationRun,
  onOpenValidationReport,
  onValidationRunsNextPage,
  onValidationRunsPreviousPage,
  onValidationRunsPageChange,
  onSelectedDeltaTableChange,
  onSelectedValidationTableChange,
  onSelectedReconciliationTableChange,
  serverError,
  fieldErrors,
  isStartValidationRunPending = false,
  isStartReconciliationRunPending = false,
  isCancelValidationRunPending = false,
  isOpenValidationReportPending = false,
  isValidationRunsFetching = false,
}: AdminSyncWorkerSettingsCardProps) {
  const { t, i18n } = useTranslation();
  const form = useForm<SyncWorkerSettingsFormValues>({
    defaultValues: settings
      ? normalizeSyncWorkerSettingsFormValues(settings)
      : {
          workerRuntimeEnabled: false,
          apiEnqueueEnabled: false,
          syncPipelineEnabled: false,
          deleteExecutionEnabled: false,
          deleteBacklogCron: '* * * * *',
          deleteBacklogLeaseMs: 60000,
          deleteBacklogMaxAttempts: 10,
          deleteBacklogBaseBackoffSeconds: 5,
          deleteBacklogMaxBackoffSeconds: 300,
          deleteVerificationTimeoutMs: 20000,
          deleteBacklogMaxRowsPerRun: 10,
          deleteBacklogClaimBatchSize: 5,
          deleteBacklogMaxDurationMs: 30000,
          deleteWebhookInboxMaxRecordsPerRun: 25,
          deleteWebhookInboxMaxIdsPerRecord: 100,
          createUpdateWebhookInboxMaxRecordsPerRun: 25,
          deleteWebhookInboxBulkAckMinRecords: WEBHOOK_INBOX_BULK_ACK_MIN_RECORDS_DEFAULT,
          createUpdateWebhookInboxBulkAckMinRecords: WEBHOOK_INBOX_BULK_ACK_MIN_RECORDS_DEFAULT,
          snapshotExecutionEnabled: true,
          snapshotSupervisorIntervalSeconds: 30,
          snapshotJobsFreshnessSlaSeconds: 30,
          snapshotDataTypesFreshnessSlaSeconds: 120,
          snapshotRelationQueueFreshnessSlaSeconds: 60,
          snapshotLeaseWindowSeconds: 45,
          snapshotHeartbeatIntervalSeconds: 5,
          globalDeltaCron: '0 * * * *',
          inventoryPostgresCountEnabled: true,
          inventoryPostgresCountCron: '',
          inventoryBubbleCountEnabled: true,
          inventoryBubbleCountCron: '',
          analyticsGearEnabled: false,
          inventoryCountsStaleWarningThresholdMinutes: 60,
          syncMatchFreshnessHours: 24,
          columnParityRefreshEnabled: false,
          columnParityRefreshCron: '0 */6 * * *',
          columnParityRefreshMaxAttempts: 3,
          columnParityRefreshBaseBackoffSeconds: 60,
          columnParityRefreshMaxBackoffSeconds: 1800,
          dataValidationEnabled: false,
          dataValidationScheduleEnabled: false,
          dataValidationScheduleCron: '0 3 1 * *',
          dataValidationLookbackDays: 30,
          dataValidationSafetyLagDays: 2,
          dataValidationRepairWaitMinutes: 60,
          dataValidationMaxRowsPerTable: 10000,
          dataValidationDirtyRecheckAttempts: 3,
          dataValidationMaxTablesPerCampaign: 25,
          dataValidationMaxSampleLimit: 1000,
          dataValidationDefaultChunkSize: 100,
          dataValidationBubbleRequestConcurrency: 1,
          dataValidationBubbleRequestsPerMinute: 60,
          dataValidationReportRetentionDays: 30,
          dataValidationFullScanEnabled: false,
          dataValidationLeaseMs: 120000,
          dataValidationCleanupBatchSize: 25,
          dataValidationMaxReportBytes: 5242880,
          dataValidationSnapshotRetentionDays: 7,
          dataValidationDirtySnapshotRetentionDays: 7,
          dataValidationRawReportRetentionDays: 30,
          dataValidationFinalReportRetentionDays: 90,
          dataValidationFreshnessDays: 35,
          refreshBatchSize: 10,
          refreshBatchDelayMs: 100,
          relationRefreshCron: '*/15 * * * *',
          relationRefreshBatchLimit: 100,
          relationRefreshLeaseMs: 60000,
          relationRefreshLockTimeoutMs: 1500,
          relationRefreshStatementTimeoutMs: 10000,
          relationRefreshIdleInTxTimeoutMs: 5000,
          relationRefreshEnabled: false,
          relationRefreshBulkEnabled: false,
          relationRefreshBulkCron: '*/5 * * * *',
          relationRefreshBulkMicrobatchSize: RELATION_REFRESH_BULK_MICROBATCH_SIZE_MAX,
          relationRefreshBulkTimeBudgetMs: 30000,
          relationRefreshBulkLeaseMs: 120000,
          relationSnapshotExactRecountEnabled: true,
          relationSnapshotExactRecountCron: '0 */4 * * *',
          relationSnapshotExactRecountStatementTimeoutMs: 20000,
          relationSnapshotExactRecountMaxTablesPerRun: 25,
          exactIdSyncEnabled: true,
          exactIdTerminalRetentionDays: 7,
          exactIdNotFoundClassifierCron: '*/1 * * * *',
          exactIdNotFoundClassifierSafetyLagMinutes: 1,
          retentionCleanupIntervalMinutes: 30,
          syncHistoryCleanupBatchSize: 500,
          workerIncidentsCleanupBatchSize: 500,
          syncTableQueueCleanupBatchSize: 500,
          syncExactIdQueueCleanupBatchSize: 500,
          jobRunsCompletedCleanupBatchSize: 500,
          jobRunsFailedCancelledCleanupBatchSize: 500,
          syncCountAuditLeaseCleanupBatchSize: 500,
          relationDoneRowsCleanupBatchSize: 500,
          deletedRelationCleanupQueueBatchSize: 500,
          syncReconciliationRunsCleanupBatchSize: 500,
          syncEntityReviewBacklogCleanupBatchSize: 500,
          bubbleMutationSuccessCleanupBatchSize: 500,
          bubbleMutationNonSuccessRedactionBatchSize: 500,
          webhookInboxLedgerCleanupBatchSize: 1000,
          syncTableQueueTotalExecutionsLimit: 2,
          syncExactIdQueueTotalExecutionsLimit: 2,
          relationRefreshQueueTotalExecutionsLimit: 2,
          mediaOwnerSyncQueueTotalExecutionsLimit: 2,
          mediaImportQueueTotalExecutionsLimit: 2,
          webhookDeltaFallbackEnabled: true,
          relationDeadRepairEnabled: false,
          relationDeadRepairBatchSize: 25,
          relationDeadRepairTimeBudgetMs: 30000,
          relationDeadRepairBubbleRateLimitPerMinute: 60,
          relationDeadRepairLeaseMs: 120000,
          relationDeadRepairMaxAttempts: 10,
          relationDeadRepairRetentionDays: 7,
          relationDeadRepairRootCron: '* * * * *',
          relationDeadRepairContinuationDelayMs: 5000,
          deletedRelationCleanupEnabled: false,
          deletedRelationCleanupBatchSize: 25,
          deletedRelationCleanupRateLimitPerMinute: 60,
          deletedRelationCleanupLeaseMs: 120000,
          deletedRelationCleanupMaxAttempts: 10,
          deletedRelationCleanupRetentionDays: 7,
          syncReconciliationMaxIdsPerBubbleRecheckBatch: 50,
          syncReconciliationMaxConflictRecheckBatchSize: 50,
          syncReconciliationMaxPagesPerBucket: 50,
          syncReconciliationMaxBucketsPerRun: 1,
          syncReconciliationMaxActionsPerRun: 1000,
          syncReconciliationMaxDeleteActionsPerRun: 500,
          syncReconciliationMaxExactIdActionsPerRun: 500,
          syncReconciliationMaxDurationMs: 300000,
          syncReconciliationSafetyLagMinutes: 10,
          syncReconciliationBucketMaxAttempts: 5,
          syncReconciliationActionRetentionDays: 14,
          syncReconciliationTierMaxRunsPerTick: 5,
          syncReconciliationTierDefaultLookbackHours: 24,
          syncSymmetricReconciliationEnabled: false,
          countMismatchDelayedRecountDelayMinutes: 5,
          mirrorDeltaPageSize: 100,
          mirrorDeltaBatchSize: 50,
          mirrorDeltaWorkBatchSize: 1,
          mirrorDeltaPollIntervalSeconds: 2,
          maxActiveSyncTables: 3,
          exactIdMaxActiveTables: 2,
          deltaMaxActiveTables: 1,
          standardMainChunkSize: 100,
          heavyRelationMainChunkSize: 25,
          standardPostprocessMicrobatchSize: 50,
          heavyRelationPostprocessMicrobatchSize: 25,
          postprocessLeaseMs: 120000,
          postprocessTimeBudgetMs: 60000,
          mediaImportSmallConcurrency: 2,
          mediaImportSchedulerEnabled: true,
          mediaOwnerSyncSchedulerEnabled: true,
          mediaOwnerMissingReconcileIntervalMinutes: 240,
          mediaOwnerSyncClaimBatchSize: 1,
          mediaOwnerSyncProcessConcurrency: 1,
          mediaOwnerSyncMaxActive: 1,
          mediaOwnerSyncTickTimeBudgetMs: 5_000,
          mediaOwnerSyncDbPressureBreakerEnabled: true,
          mediaOwnerSyncClaimDbPressureThresholdMs: 1_500,
          testModeEnabled: false,
          testModeBubbleKey: null,
          testModeRecordLimit: 25,
        },
  });

  const [globalDeltaInterval, setGlobalDeltaInterval] = useState<IntervalState>(() =>
    settings ? buildIntervalState(settings.globalDeltaCron) : buildIntervalState('0 * * * *')
  );
  const [deleteBacklogInterval, setDeleteBacklogInterval] = useState<IntervalState>(() =>
    settings ? buildIntervalState(settings.deleteBacklogCron) : buildIntervalState('* * * * *')
  );
  const [columnParityInterval, setColumnParityInterval] = useState<IntervalState>(() =>
    settings
      ? buildIntervalState(settings.columnParityRefreshCron)
      : buildIntervalState('0 */6 * * *')
  );
  const [relationRefreshInterval, setRelationRefreshInterval] = useState<IntervalState>(() =>
    settings ? buildIntervalState(settings.relationRefreshCron) : buildIntervalState('*/15 * * * *')
  );
  const [relationRefreshBulkInterval, setRelationRefreshBulkInterval] = useState<IntervalState>(
    () =>
      settings
        ? buildIntervalState(settings.relationRefreshBulkCron)
        : buildIntervalState('*/5 * * * *')
  );
  const [relationSnapshotExactRecountInterval, setRelationSnapshotExactRecountInterval] =
    useState<IntervalState>(() =>
      settings
        ? buildIntervalState(settings.relationSnapshotExactRecountCron)
        : buildIntervalState('0 */4 * * *')
    );

  useEffect(() => {
    if (settings) {
      form.reset(normalizeSyncWorkerSettingsFormValues(settings));
      setGlobalDeltaInterval(buildIntervalState(settings.globalDeltaCron));
      setDeleteBacklogInterval(buildIntervalState(settings.deleteBacklogCron));
      setColumnParityInterval(buildIntervalState(settings.columnParityRefreshCron));
      setRelationRefreshInterval(buildIntervalState(settings.relationRefreshCron));
      setRelationRefreshBulkInterval(buildIntervalState(settings.relationRefreshBulkCron));
      setRelationSnapshotExactRecountInterval(
        buildIntervalState(settings.relationSnapshotExactRecountCron)
      );
    }
  }, [form, settings]);

  useEffect(() => {
    if (!fieldErrors) return;
    for (const [field, message] of Object.entries(fieldErrors)) {
      if (!message) continue;
      form.setError(field as keyof SyncWorkerSettingsFormValues, {
        type: 'manual',
        message,
      });
    }
  }, [fieldErrors, form]);

  const testModeEnabled = form.watch('testModeEnabled');
  const testModeBubbleKey = form.watch('testModeBubbleKey');
  const workerSyncEnabled = form.watch('workerRuntimeEnabled');
  const syncPipelineEnabled = form.watch('syncPipelineEnabled');
  const syncSymmetricReconciliationEnabled = form.watch('syncSymmetricReconciliationEnabled');
  const dataValidationEnabled = form.watch('dataValidationEnabled');
  const dataValidationMaxSampleLimit = form.watch('dataValidationMaxSampleLimit');
  const dataValidationDefaultChunkSize = form.watch('dataValidationDefaultChunkSize');
  const dataValidationBubbleRequestConcurrency = form.watch(
    'dataValidationBubbleRequestConcurrency'
  );
  const dataValidationBubbleRequestsPerMinute = form.watch('dataValidationBubbleRequestsPerMinute');
  const dataValidationFullScanEnabled = form.watch('dataValidationFullScanEnabled');
  const manualDeltaActionsDisabled = !workerSyncEnabled || !syncPipelineEnabled;
  const persistedReconciliationEnabled =
    effectiveSettings?.syncSymmetricReconciliationEnabled ?? syncSymmetricReconciliationEnabled;
  const manualReconciliationActionsDisabled =
    !workerSyncEnabled || !syncPipelineEnabled || !persistedReconciliationEnabled;
  const validationActionsDisabled = !workerSyncEnabled || !dataValidationEnabled;
  const validationRunsPageSize = recentValidationRunsPagination?.limit ?? 10;
  const validationRunsHasMore = recentValidationRunsPagination?.hasMore ?? false;
  const validationRunsCurrentPage = Math.max(1, recentValidationRunsPage);
  const validationRunsTotalItems = validationRunsHasMore
    ? validationRunsCurrentPage * validationRunsPageSize + 1
    : Math.max(
        (validationRunsCurrentPage - 1) * validationRunsPageSize + recentValidationRuns.length,
        recentValidationRuns.length
      );
  const snapshotLaneRequestedEnabled = form.watch('snapshotExecutionEnabled');
  const clampedFields = settingsMeta?.clampedFields ?? {};
  const [validationMode, setValidationMode] = useState<'sample' | 'full'>('sample');
  const [validationLimit, setValidationLimit] = useState(100);
  const [confirmFullValidation, setConfirmFullValidation] = useState(false);
  const [validationScopeEnabled, setValidationScopeEnabled] = useState(false);
  const [validationScopeBasis, setValidationScopeBasis] =
    useState<ValidationScopeBasis>('bubble_modified_at');
  const [validationScopeRange, setValidationScopeRange] = useState(getDefaultValidationScopeRange);
  const [validationScopeLabel, setValidationScopeLabel] = useState('');
  const [manualReconciliationMode, setManualReconciliationMode] =
    useState<SyncReconciliationRunMode>('dry_run');
  const [manualReconciliationWindowMode, setManualReconciliationWindowMode] =
    useState<ManualReconciliationWindowMode>('recent_window');
  const [manualReconciliationRange, setManualReconciliationRange] = useState(
    getDefaultManualReconciliationRange
  );
  const [manualReconciliationReason, setManualReconciliationReason] = useState(
    'operator_manual_reconciliation'
  );
  const [manualReconciliationFullConfirmed, setManualReconciliationFullConfirmed] = useState(false);
  const validationScopeFromIso = validationScopeEnabled
    ? toUtcIsoFromDateOnly(validationScopeRange.from)
    : null;
  const validationScopeToIso = validationScopeEnabled
    ? toExclusiveUtcIsoFromDateOnly(validationScopeRange.to)
    : null;
  const validationScopeInvalid =
    validationScopeEnabled &&
    (validationScopeFromIso === null ||
      validationScopeToIso === null ||
      new Date(validationScopeFromIso) >= new Date(validationScopeToIso));
  const selectedReconciliationTarget =
    reconciliationTableOptions.find((option) => option.value === selectedReconciliationTable) ??
    null;
  const manualReconciliationWindowStart =
    manualReconciliationWindowMode === 'full_table'
      ? FULL_TABLE_RECONCILIATION_WINDOW_START
      : toUtcIsoFromDateOnly(manualReconciliationRange.from);
  const manualReconciliationSafetyLagMinutes =
    effectiveSettings?.syncReconciliationSafetyLagMinutes ??
    form.watch('syncReconciliationSafetyLagMinutes');
  const manualReconciliationLatestAllowedWindowEnd = buildSafetyLaggedWindowEndDate(
    manualReconciliationSafetyLagMinutes
  );
  const manualReconciliationDateWindowMaxDate = getLatestManualReconciliationDateWindowEndDate(
    manualReconciliationSafetyLagMinutes
  );
  const manualReconciliationWindowEnd =
    manualReconciliationWindowMode === 'full_table'
      ? manualReconciliationLatestAllowedWindowEnd.toISOString()
      : toExclusiveUtcIsoFromDateOnly(manualReconciliationRange.to);
  const manualReconciliationWindowInvalid =
    manualReconciliationWindowStart === null ||
    manualReconciliationWindowEnd === null ||
    new Date(manualReconciliationWindowStart) >= new Date(manualReconciliationWindowEnd) ||
    new Date(manualReconciliationWindowEnd) > manualReconciliationLatestAllowedWindowEnd;
  const manualReconciliationFullRunBlocked =
    manualReconciliationWindowMode === 'full_table' && !manualReconciliationFullConfirmed;

  const hasUnsupportedIntervals =
    globalDeltaInterval.kind === 'unsupported' ||
    deleteBacklogInterval.kind === 'unsupported' ||
    columnParityInterval.kind === 'unsupported' ||
    relationRefreshInterval.kind === 'unsupported' ||
    relationRefreshBulkInterval.kind === 'unsupported' ||
    relationSnapshotExactRecountInterval.kind === 'unsupported';

  const formatSecondsLabel = (seconds: number): string => {
    if (seconds % 60 === 0) {
      return t('admin.syncWorker.units.minutesCompact', {
        defaultValue: '{{value}} min',
        value: String(seconds / 60),
      });
    }

    return t('admin.syncWorker.units.secondsCompact', {
      defaultValue: '{{value}} sec',
      value: String(seconds),
    });
  };

  const formatMillisecondsLabel = (milliseconds: number): string => {
    if (milliseconds % 1000 === 0) {
      return formatSecondsLabel(milliseconds / 1000);
    }

    return t('admin.syncWorker.units.millisecondsCompact', {
      defaultValue: '{{value}} ms',
      value: String(milliseconds),
    });
  };

  const formatCronLabel = (cron: string): string => {
    const interval = parseCronInterval(cron);
    if (!interval) {
      return cron;
    }

    if (interval.kind === 'minutes') {
      if (interval.value === 1) {
        return t('admin.syncWorker.interval.everyMinute', {
          defaultValue: 'Every 1 minute',
        });
      }
      return t('admin.syncWorker.interval.everyMinutesValue', {
        defaultValue: 'Every {{value}} minutes',
        value: String(interval.value),
      });
    }

    if (interval.kind === 'hours') {
      if (interval.value === 1) {
        return t('admin.syncWorker.interval.everyHour', {
          defaultValue: 'Every hour',
        });
      }
      return t('admin.syncWorker.interval.everyHoursValue', {
        defaultValue: 'Every {{value}} hours',
        value: String(interval.value),
      });
    }

    const hour = typeof interval.hour === 'number' ? interval.hour : 0;
    return t('admin.syncWorker.interval.everyDayAt', {
      defaultValue: 'Every day at {{hour}}:00 UTC',
      hour: String(hour).padStart(2, '0'),
    });
  };

  const renderEffectiveRuntimeNote = (
    field: keyof SyncWorkerSettingsFormValues,
    formatValue: (value: SyncWorkerSettingsFormValues[keyof SyncWorkerSettingsFormValues]) => string
  ) => {
    if (!settings || !effectiveSettings) {
      return null;
    }

    const requestedValue = settings[field];
    const effectiveValue = effectiveSettings[field];
    if (effectiveValue === undefined) {
      return null;
    }
    const clampMeta = Object.prototype.hasOwnProperty.call(clampedFields, field)
      ? clampedFields[field]
      : null;

    if (requestedValue === effectiveValue && clampMeta === null) {
      return null;
    }

    const reasonSuffix =
      clampMeta !== null && clampMeta.envKey !== undefined
        ? t('admin.syncWorker.effectiveRuntimeClampEnv', {
            defaultValue: 'Capped by {{envKey}}.',
            envKey: clampMeta.envKey,
          })
        : clampMeta !== null
          ? t('admin.syncWorker.effectiveRuntimeClampRuntime', {
              defaultValue: 'Adjusted by runtime safety rules.',
            })
          : '';

    return (
      <Text size="sm" color="muted">
        {t('admin.syncWorker.effectiveRuntimeNote', {
          defaultValue: 'Effective runtime: {{effective}} (requested: {{requested}}). {{reason}}',
          effective: formatValue(effectiveValue),
          requested: formatValue(requestedValue),
          reason: reasonSuffix,
        }).trim()}
      </Text>
    );
  };

  const renderSnapshotExecutionNotice = () => {
    const reason = settingsMeta?.snapshotExecution.reason ?? null;

    if (reason === 'global_worker_execution_disabled') {
      return (
        <Alert>
          <AlertDescription>
            {t(
              'admin.syncWorker.snapshotExecutionGlobalOverride',
              'Snapshot refresh is currently overridden because Worker Sync Enabled is off. The requested snapshot setting is still saved, but the effective runtime stays disabled until global worker execution is restored.'
            )}
          </AlertDescription>
        </Alert>
      );
    }

    if (reason === 'snapshot_execution_disabled' || !snapshotLaneRequestedEnabled) {
      return (
        <Alert>
          <AlertDescription>
            {t(
              'admin.syncWorker.snapshotExecutionDisabled',
              'Snapshot refresh is intentionally disabled for the snapshot lane. Worker observability will report this as disabled, not stale.'
            )}
          </AlertDescription>
        </Alert>
      );
    }

    return null;
  };

  const buildDailyHelperText = (state: IntervalState) => {
    if (state.kind !== 'days') return null;
    const hour = state.dayHour ?? 0;
    return t('admin.syncWorker.interval.dailyAt', {
      defaultValue: 'Runs daily at {{hour}}:00 UTC',
      hour: hour.toString().padStart(2, '0'),
    });
  };

  const formatValidationRunStatus = (run: SyncDataValidationRun): string => {
    if (run.status === 'completed' && run.dataOutcome === 'findings') {
      return t('admin.syncWorker.dataValidationCompletedWithFindings', {
        defaultValue: 'Completed with findings',
      });
    }
    if (run.status === 'completed') {
      return t('admin.syncWorker.dataValidationCompleted', {
        defaultValue: 'Completed',
      });
    }
    if (run.status === 'queued') {
      return t('admin.syncWorker.dataValidationQueued', { defaultValue: 'Queued' });
    }
    if (run.status === 'running') {
      return t('admin.syncWorker.dataValidationRunning', { defaultValue: 'Running' });
    }
    if (run.status === 'cancelled') {
      return t('admin.syncWorker.dataValidationCancelled', { defaultValue: 'Cancelled' });
    }
    if (run.status === 'failed') {
      return t('admin.syncWorker.dataValidationFailed', { defaultValue: 'Failed' });
    }
    return run.status;
  };

  const formatValidationReportState = (run: SyncDataValidationRun): string => {
    if (run.reportState === 'available') {
      return t('admin.syncWorker.dataValidationReportAvailable', {
        defaultValue: 'Report available',
      });
    }
    if (run.reportState === 'expired') {
      return t('admin.syncWorker.dataValidationReportExpired', {
        defaultValue: 'Report expired',
      });
    }
    if (run.reportState === 'cleanup_failed') {
      return t('admin.syncWorker.dataValidationReportCleanupFailed', {
        defaultValue: 'Cleanup failed',
      });
    }
    return t('admin.syncWorker.dataValidationReportPending', {
      defaultValue: 'Report pending',
    });
  };

  const formatValidationAcceptanceState = (run: SyncDataValidationRun): string => {
    const state = run.acceptanceStatus?.state ?? null;
    if (state === 'accepted') {
      return t('admin.syncWorker.dataValidationAcceptanceAccepted', 'Accepted');
    }
    if (state === 'pending_repair') {
      return t('admin.syncWorker.dataValidationAcceptancePendingRepair', 'Pending repair');
    }
    if (state === 'needs_followup') {
      return t('admin.syncWorker.dataValidationAcceptanceNeedsFollowup', 'Needs follow-up');
    }
    if (state === 'evidence_unavailable') {
      return t(
        'admin.syncWorker.dataValidationAcceptanceEvidenceUnavailable',
        'Evidence unavailable'
      );
    }
    if (state === 'overflow_blocked') {
      return t('admin.syncWorker.dataValidationAcceptanceOverflowBlocked', 'Overflow blocked');
    }
    return t('admin.syncWorker.dataValidationAcceptanceIncomplete', 'Incomplete');
  };

  const formatValidationAcceptanceLabel = (run: SyncDataValidationRun): string => {
    const label = run.acceptanceStatus?.acceptanceLabel ?? null;
    if (label === 'scoped_source_window') {
      return t('admin.syncWorker.dataValidationAcceptanceScopedSource', 'Scoped source window');
    }
    if (label === 'local_sync_window_only') {
      return t('admin.syncWorker.dataValidationAcceptanceLocalSync', 'Local-sync window only');
    }
    return t('admin.syncWorker.dataValidationAcceptanceFullTableCandidate', 'Full-table candidate');
  };

  const formatValidationClassificationGranularity = (run: SyncDataValidationRun): string => {
    const granularity = run.acceptanceStatus?.classificationGranularity ?? null;
    if (granularity === 'record') {
      return t('admin.syncWorker.dataValidationGranularityRecord', 'Record-level');
    }
    if (granularity === 'group') {
      return t('admin.syncWorker.dataValidationGranularityGroup', 'Group-level');
    }
    if (granularity === 'overflow_blocked') {
      return t('admin.syncWorker.dataValidationGranularityOverflowBlocked', 'Overflow blocked');
    }
    return t('admin.syncWorker.dataValidationGranularityPending', 'Pending');
  };

  const renderValidationAcceptanceReadout = (run: SyncDataValidationRun) => {
    const acceptance = run.acceptanceStatus ?? null;
    if (acceptance === null) {
      return null;
    }
    const canonicalEvidenceAvailable = acceptance.canonicalEvidenceRef !== null;
    const blockers = acceptance.blockers.slice(0, 2);

    return (
      <div className="grid gap-2 rounded-md bg-muted/40 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge appearance="outline">{formatValidationAcceptanceState(run)}</Badge>
          <Badge appearance="outline">{formatValidationAcceptanceLabel(run)}</Badge>
        </div>
        <div className="grid gap-1 text-sm text-muted-foreground sm:grid-cols-2">
          <Text size="sm" color="muted">
            {t('admin.syncWorker.dataValidationEvidenceState', {
              defaultValue: 'Evidence: {{state}}',
              state: formatValidationReportState(run),
            })}
          </Text>
          <Text size="sm" color="muted">
            {t('admin.syncWorker.dataValidationGranularityState', {
              defaultValue: 'Granularity: {{granularity}}',
              granularity: formatValidationClassificationGranularity(run),
            })}
          </Text>
          <Text size="sm" color="muted">
            {t('admin.syncWorker.dataValidationCanonicalEvidenceState', {
              defaultValue: 'Canonical evidence: {{state}}',
              state: canonicalEvidenceAvailable
                ? t('admin.syncWorker.dataValidationCanonicalEvidenceAvailable', 'available')
                : t('admin.syncWorker.dataValidationCanonicalEvidenceMissing', 'missing'),
            })}
          </Text>
          <Text size="sm" color="muted">
            {t('admin.syncWorker.dataValidationScopeStatus', {
              defaultValue: 'Scope: {{scope}}',
              scope: acceptance.scopeLabel ?? formatValidationAcceptanceLabel(run),
            })}
          </Text>
        </div>
        {blockers.length > 0 ? (
          <div className="grid gap-1">
            {blockers.map((blocker) => (
              <Text key={blocker} size="sm" color="muted">
                {blocker}
              </Text>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  const calculateValidationProgress = (run: SyncDataValidationRun): number => {
    if (run.selectedCount <= 0) {
      return run.status === 'completed' ? 100 : 0;
    }
    return Math.min(100, Math.round((run.processedCount / run.selectedCount) * 100));
  };

  const renderIntervalControls = (
    idPrefix: string,
    state: IntervalState,
    onChange: (next: IntervalState) => void,
    onCronChange: (cron: string) => void
  ) => {
    const setKind = (kind: IntervalKind) => {
      const next: IntervalState = {
        ...state,
        kind,
      };
      const cron = resolveIntervalCron(next);
      onChange(next);
      onCronChange(cron);
    };

    const setMinutes = (value: SyncWorkerMinutesInterval) => {
      const next = { ...state, kind: 'minutes' as const, minutesValue: value };
      onChange(next);
      onCronChange(resolveIntervalCron(next));
    };

    const setHours = (value: number) => {
      const next = { ...state, kind: 'hours' as const, hoursValue: value };
      onChange(next);
      onCronChange(resolveIntervalCron(next));
    };

    const setDayHour = (value: number) => {
      const next = { ...state, kind: 'days' as const, dayHour: value };
      onChange(next);
      onCronChange(resolveIntervalCron(next));
    };

    return (
      <Stack gap="sm">
        <Select
          value={state.kind === 'unsupported' ? '' : state.kind}
          onValueChange={(value) => {
            setKind(value as IntervalKind);
          }}
          disabled={isReadOnly || isLoading}
        >
          <SelectTrigger id={`${idPrefix}-interval-kind`}>
            <SelectValue
              placeholder={t('admin.syncWorker.interval.placeholder', 'Choose interval type')}
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="minutes">
              {t('admin.syncWorker.interval.everyMinutes', 'Every N minutes')}
            </SelectItem>
            <SelectItem value="hours">
              {t('admin.syncWorker.interval.everyHours', 'Every N hours')}
            </SelectItem>
            <SelectItem value="days">
              {t('admin.syncWorker.interval.everyDay', 'Every day')}
            </SelectItem>
          </SelectContent>
        </Select>

        {state.kind === 'minutes' && (
          <Select
            value={String(state.minutesValue)}
            onValueChange={(value) => {
              setMinutes(Number(value) as SyncWorkerMinutesInterval);
            }}
            disabled={isReadOnly || isLoading}
          >
            <SelectTrigger id={`${idPrefix}-interval-minutes`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-64 overflow-y-auto">
              {MINUTES_INTERVAL_VALUES.map((value) => (
                <SelectItem key={value} value={String(value)}>
                  {String(value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {state.kind === 'hours' && (
          <Input
            id={`${idPrefix}-interval-hours`}
            type="number"
            min={1}
            max={23}
            step={1}
            value={state.hoursValue}
            onChange={(event) => {
              const value = Number(event.target.value);
              if (Number.isNaN(value)) return;
              setHours(Math.max(1, Math.min(23, Math.trunc(value))));
            }}
            disabled={isReadOnly || isLoading}
          />
        )}

        {state.kind === 'days' && (
          <>
            <Input
              id={`${idPrefix}-interval-day-hour`}
              type="number"
              min={0}
              max={23}
              step={1}
              value={state.dayHour ?? 0}
              onChange={(event) => {
                const value = Number(event.target.value);
                if (Number.isNaN(value)) return;
                setDayHour(Math.max(0, Math.min(23, Math.trunc(value))));
              }}
              disabled={isReadOnly || isLoading}
            />
            {buildDailyHelperText(state) ? (
              <Text size="sm" color="muted">
                {buildDailyHelperText(state)}
              </Text>
            ) : null}
          </>
        )}
      </Stack>
    );
  };

  const handleSave = form.handleSubmit(async (values) => {
    const operatorPayload = Object.fromEntries(
      Object.entries(values).filter(([field]) => !OPERATOR_HIDDEN_SETTING_FIELDS.has(field))
    ) as SyncWorkerSettingsUpdatePayload;
    await onSubmit(operatorPayload);
  });

  return (
    <Form {...form}>
      <form
        className="space-y-6"
        onSubmit={(event) => {
          void handleSave(event);
        }}
      >
        <Card>
          <CardHeader>
            <div className="flex min-w-0 flex-wrap items-start justify-between gap-3 sm:items-center">
              <CardTitle className="min-w-0 break-words">
                {t('admin.syncWorker.syncTitle', 'Worker Control Center')}
              </CardTitle>
              <Badge appearance="outline">
                {t('admin.syncWorker.environmentLabel', {
                  defaultValue: 'Environment: {{env}}',
                  env: environmentLabel,
                })}
              </Badge>
            </div>
            <Text size="sm" color="muted">
              {t(
                'admin.syncWorker.syncDescription',
                'Global worker control. Start here to see whether worker-owned background processing, schedules, and manual actions are allowed to run.'
              )}
            </Text>
            {settingsMeta ? (
              <Text size="sm" color="muted">
                {t('admin.syncWorker.lastUpdated', {
                  defaultValue: 'Last updated: {{updatedAt}}',
                  updatedAt: formatUtcDateTime(settingsMeta.updatedAt, i18n.language),
                })}
              </Text>
            ) : null}
          </CardHeader>
          <CardContent>
            {serverError ? (
              <Alert variant="destructive" aria-live="assertive">
                <AlertDescription>{serverError}</AlertDescription>
              </Alert>
            ) : null}

            {hasUnsupportedIntervals ? (
              <Alert variant="destructive" className="mt-4">
                <AlertDescription>
                  {t(
                    'admin.syncWorker.unsupportedCron',
                    'At least one stored schedule uses an unsupported cron format. Choose one of the supported interval modes before saving.'
                  )}
                </AlertDescription>
              </Alert>
            ) : null}

            <div className="mt-6 grid gap-4">
              <div className="rounded-lg border border-border p-4">
                <Stack gap="md">
                  <SectionHeading
                    title={t('admin.syncWorker.workerSectionTitle', 'Worker execution')}
                    description={t(
                      'admin.syncWorker.workerSectionDescription',
                      'Master runtime switch for every worker-owned business lane.'
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="workerRuntimeEnabled"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center justify-between gap-4">
                          <div className="min-w-0">
                            <SettingLabel
                              label={t(
                                'admin.syncWorker.workerRuntimeEnabled',
                                'Worker Runtime Enabled'
                              )}
                              help={t(
                                'admin.syncWorker.workerRuntimeEnabledDescription',
                                'Master kill switch: when off, all background worker processing, schedules, and manual run buttons stay disabled.'
                              )}
                            />
                            <FormDescription>
                              {t(
                                'admin.syncWorker.workerRuntimeEnabledDescription',
                                'Master kill switch: when off, all background worker processing, schedules, and manual run buttons stay disabled.'
                              )}
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              disabled={isReadOnly || isLoading}
                            />
                          </FormControl>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </Stack>
              </div>

              <div className="rounded-lg border border-border p-4">
                <Stack gap="md">
                  <SectionHeading
                    title={t('admin.syncWorker.slackReportTitle', 'Slack synchronization report')}
                    description={t('admin.syncWorker.slackReportDescription', {
                      defaultValue:
                        'Sends a safe report for {{environment}} only. This environment never reads or reports another environment.',
                      environment: environmentLabel.toUpperCase(),
                    })}
                  />
                  <div className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                    <Text size="sm" color="muted">
                      {t('admin.syncWorker.slackReportSchedule', {
                        defaultValue: 'Automatic report: daily at 09:00 Moscow time',
                      })}
                    </Text>
                    <Text size="sm" color="muted">
                      {slackReport?.lastDelivery
                        ? t('admin.syncWorker.slackReportLastDelivery', {
                            defaultValue: 'Last delivery: {{time}} — {{status}}',
                            time: formatUtcDateTime(
                              slackReport.lastDelivery.deliveredAt,
                              i18n.language
                            ),
                            status: t(
                              `admin.syncWorker.slackReportStatus.${slackReport.lastDelivery.status}`,
                              slackReport.lastDelivery.status
                            ),
                          })
                        : t(
                            'admin.syncWorker.slackReportNoDelivery',
                            'No delivery has been recorded yet.'
                          )}
                    </Text>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <a
                      href="https://molchanovs.slack.com/archives/C0BLF8TQHHD"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex min-h-11 items-center text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {t('admin.syncWorker.slackReportOpenChannel', 'Open Slack channel')}
                    </a>
                    <Button
                      type="button"
                      variant="outline"
                      className="min-h-11"
                      disabled={
                        isReadOnly ||
                        isSlackReportPending ||
                        slackReport?.available !== true ||
                        onSendSlackReport === undefined
                      }
                      onClick={() => {
                        void onSendSlackReport?.();
                      }}
                    >
                      {isSlackReportPending
                        ? t('admin.syncWorker.slackReportSending', 'Sending report...')
                        : t('admin.syncWorker.slackReportSend', {
                            defaultValue: 'Send {{environment}} report now',
                            environment: environmentLabel.toUpperCase(),
                          })}
                    </Button>
                  </div>
                </Stack>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('admin.syncWorker.runtimeTitle', 'Runtime Settings')}</CardTitle>
            <Text size="sm" color="muted">
              {t(
                'admin.syncWorker.runtimeDescription',
                'Detailed runtime settings grouped by operator intent: sync, relation refresh, validation, analytics, operator tools, and counts.'
              )}
            </Text>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="sync" className="w-full">
              <TabsList
                data-testid="runtime-settings-tabs-list"
                variant="secondary"
                className="w-full min-w-0 justify-start overflow-x-auto overflow-y-hidden"
              >
                <TabsTrigger
                  variant="secondary"
                  value="sync"
                  className="h-auto min-h-11 shrink-0 whitespace-normal py-2 text-left sm:text-center"
                >
                  {t('admin.syncWorker.runtimeTabs.sync', 'Sync')}
                </TabsTrigger>
                <TabsTrigger
                  variant="secondary"
                  value="relation-refresh"
                  className="h-auto min-h-11 shrink-0 whitespace-normal py-2 text-left sm:text-center"
                >
                  {t('admin.syncWorker.runtimeTabs.relationRefresh', 'Relation Refresh')}
                </TabsTrigger>
                <TabsTrigger
                  variant="secondary"
                  value="delete-lane"
                  className="h-auto min-h-11 shrink-0 whitespace-normal py-2 text-left sm:text-center"
                >
                  {t('admin.syncWorker.runtimeTabs.deleteLane', 'Delete Lane')}
                </TabsTrigger>
                <TabsTrigger
                  variant="secondary"
                  value="maintenance"
                  className="h-auto min-h-11 shrink-0 whitespace-normal py-2 text-left sm:text-center"
                >
                  {t('admin.syncWorker.runtimeTabs.maintenance', 'Maintenance')}
                </TabsTrigger>
                <TabsTrigger
                  variant="secondary"
                  value="media-import"
                  className="h-auto min-h-11 shrink-0 whitespace-normal py-2 text-left sm:text-center"
                >
                  {t('admin.syncWorker.runtimeTabs.mediaImport', 'Media Import')}
                </TabsTrigger>
                <TabsTrigger
                  variant="secondary"
                  value="snapshot-lane"
                  className="h-auto min-h-11 shrink-0 whitespace-normal py-2 text-left sm:text-center"
                >
                  {t('admin.syncWorker.runtimeTabs.snapshotLane', 'Snapshot Lane')}
                </TabsTrigger>
                <TabsTrigger
                  variant="secondary"
                  value="validation"
                  className="h-auto min-h-11 shrink-0 whitespace-normal py-2 text-left sm:text-center"
                >
                  {t('admin.syncWorker.runtimeTabs.validation', 'Validation')}
                </TabsTrigger>
                <TabsTrigger
                  variant="secondary"
                  value="analytics-gear"
                  className="h-auto min-h-11 shrink-0 whitespace-normal py-2 text-left sm:text-center"
                >
                  {t('admin.syncWorker.runtimeTabs.analyticsGear', 'Analytics Gear')}
                </TabsTrigger>
                <TabsTrigger
                  variant="secondary"
                  value="operator-tools"
                  className="h-auto min-h-11 shrink-0 whitespace-normal py-2 text-left sm:text-center"
                >
                  {t('admin.syncWorker.runtimeTabs.operatorTools', 'Operator Tools')}
                </TabsTrigger>
                <TabsTrigger
                  variant="secondary"
                  value="counts"
                  className="h-auto min-h-11 shrink-0 whitespace-normal py-2 text-left sm:text-center"
                >
                  {t('admin.syncWorker.runtimeTabs.counts', 'Counts')}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="sync" className="mt-4">
                <div className="grid gap-4 lg:grid-cols-2">
                  <RuntimeFieldCard
                    title={t('admin.syncWorker.apiSectionTitle', 'Webhook and API intake')}
                    description={t(
                      'admin.syncWorker.apiSectionDescription',
                      'Controls whether the API accepts webhook and intake sync events.'
                    )}
                  >
                    <FormField
                      control={form.control}
                      name="apiEnqueueEnabled"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center justify-between gap-4">
                            <div className="min-w-0">
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.apiEnabled',
                                  'Webhook/API Intake Enabled'
                                )}
                                help={t(
                                  'admin.syncWorker.apiEnabledDescription',
                                  'When off, the API stops accepting new sync events from intake and webhook paths.'
                                )}
                              />
                              <FormDescription>
                                {t(
                                  'admin.syncWorker.apiEnabledDescription',
                                  'When off, the API stops accepting new sync events from intake and webhook paths.'
                                )}
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </RuntimeFieldCard>

                  <RuntimeFieldCard
                    title={t('admin.syncWorker.syncPipelineTitle', 'Sync pipeline')}
                    description={t(
                      'admin.syncWorker.syncPipelineDescription',
                      'Controls table import, chunk processing, post-processing, full sync runs, and watchdog recovery.'
                    )}
                  >
                    <FormField
                      control={form.control}
                      name="syncPipelineEnabled"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center justify-between gap-4">
                            <div className="min-w-0">
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.syncPipelineEnabled',
                                  'Sync pipeline enabled'
                                )}
                                help={t(
                                  'admin.syncWorker.syncPipelineEnabledDescription',
                                  'Controls table sync import, chunk processing, user post-processing, full sync runs, and the sync watchdog.'
                                )}
                              />
                              <FormDescription>
                                {t(
                                  'admin.syncWorker.syncPipelineEnabledDescription',
                                  'Controls table sync import, chunk processing, user post-processing, full sync runs, and the sync watchdog.'
                                )}
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={isReadOnly || isLoading || !workerSyncEnabled}
                              />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </RuntimeFieldCard>

                  <RuntimeFieldCard
                    title={t('admin.syncWorker.batchTuningTitle', 'Batch tuning')}
                    description={t(
                      'admin.syncWorker.batchTuningDescription',
                      'Controls how aggressively delta work is processed.'
                    )}
                  >
                    <div className="grid gap-4 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="refreshBatchSize"
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t('admin.syncWorker.batchSize', 'Refresh Batch Size')}
                              help={t(
                                'admin.syncWorker.batchSizeDescription',
                                'How many records the delta runtime processes per refresh batch.'
                              )}
                            />
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                value={field.value}
                                onChange={(event) => {
                                  field.onChange(Number(event.target.value));
                                }}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="refreshBatchDelayMs"
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t('admin.syncWorker.batchDelay', 'Refresh Batch Delay')}
                              help={t(
                                'admin.syncWorker.batchDelayDescription',
                                'Delay between refresh batches in milliseconds.'
                              )}
                            />
                            <FormControl>
                              <Input
                                type="number"
                                min={0}
                                value={field.value}
                                onChange={(event) => {
                                  field.onChange(Number(event.target.value));
                                }}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </RuntimeFieldCard>

                  <RuntimeFieldCard
                    title={t(
                      'admin.syncWorker.universalSyncRuntimeTitle',
                      'Universal sync runtime'
                    )}
                    description={t(
                      'admin.syncWorker.universalSyncRuntimeDescription',
                      'Primary runtime controls for the shared canonical chunk and postprocess pipeline.'
                    )}
                  >
                    <div className="grid gap-4 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="maxActiveSyncTables"
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t(
                                'admin.syncWorker.maxActiveSyncTables',
                                'Max Active Sync Tables'
                              )}
                              help={t(
                                'admin.syncWorker.maxActiveSyncTablesDescription',
                                'Global cap for simultaneously active sync table lanes across the worker.'
                              )}
                            />
                            <FormControl>
                              <Input
                                type="number"
                                min={2}
                                max={3}
                                value={field.value}
                                onChange={(event) => {
                                  field.onChange(Number(event.target.value));
                                }}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="postprocessLeaseMs"
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t(
                                'admin.syncWorker.postprocessLeaseMs',
                                'Postprocess Lease (ms)'
                              )}
                              help={t(
                                'admin.syncWorker.postprocessLeaseMsDescription',
                                'Lease window for the guaranteed postprocess phase before watchdog recovery may reclaim it.'
                              )}
                            />
                            <FormControl>
                              <Input
                                type="number"
                                min={1000}
                                value={field.value}
                                onChange={(event) => {
                                  field.onChange(Number(event.target.value));
                                }}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="standardMainChunkSize"
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t(
                                'admin.syncWorker.standardMainChunkSize',
                                'Standard Main Chunk Size'
                              )}
                              help={t(
                                'admin.syncWorker.standardMainChunkSizeDescription',
                                'Default chunk size for standard tables in the main checkpoint-safe sync phase.'
                              )}
                            />
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                value={field.value}
                                onChange={(event) => {
                                  field.onChange(Number(event.target.value));
                                }}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="heavyRelationMainChunkSize"
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t(
                                'admin.syncWorker.heavyRelationMainChunkSize',
                                'Heavy Relation Main Chunk Size'
                              )}
                              help={t(
                                'admin.syncWorker.heavyRelationMainChunkSizeDescription',
                                'Smaller main-phase chunk size for heavy relation tables such as courses and registrations.'
                              )}
                            />
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                value={field.value}
                                onChange={(event) => {
                                  field.onChange(Number(event.target.value));
                                }}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Text size="sm" color="muted">
                        {t(
                          'admin.syncWorker.postprocessMicrobatchDerivedNotice',
                          'Postprocess microbatch size follows the matching main chunk size automatically.'
                        )}
                      </Text>
                      <FormField
                        control={form.control}
                        name="postprocessTimeBudgetMs"
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t(
                                'admin.syncWorker.postprocessTimeBudgetMs',
                                'Postprocess Time Budget (ms)'
                              )}
                              help={t(
                                'admin.syncWorker.postprocessTimeBudgetMsDescription',
                                'Soft execution budget for one postprocess slice before it should yield and continue later.'
                              )}
                            />
                            <FormControl>
                              <Input
                                type="number"
                                min={1000}
                                value={field.value}
                                onChange={(event) => {
                                  field.onChange(Number(event.target.value));
                                }}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </RuntimeFieldCard>

                  <RuntimeFieldCard
                    title={t('admin.syncWorker.exactIdRepairTitle', 'Exact-ID repair sync')}
                    description={t(
                      'admin.syncWorker.exactIdRepairDescription',
                      'Controls targeted repair rows that sync known Bubble IDs through the existing sync worker lane.'
                    )}
                  >
                    <Stack gap="md">
                      <FormField
                        control={form.control}
                        name="exactIdSyncEnabled"
                        render={({ field }) => (
                          <FormItem className="rounded-md border border-border p-3">
                            <div className="flex items-center justify-between gap-4">
                              <div className="min-w-0">
                                <SettingLabel
                                  label={t(
                                    'admin.syncWorker.exactIdSyncEnabled',
                                    'Enable exact-ID repair'
                                  )}
                                  help={t(
                                    'admin.syncWorker.exactIdSyncEnabledDescription',
                                    'Allows pending exact-ID repair rows to run through the shared sync worker when global worker execution is enabled.'
                                  )}
                                />
                                <FormDescription>
                                  {t(
                                    'admin.syncWorker.exactIdSyncEnabledDescription',
                                    'Allows pending exact-ID repair rows to run through the shared sync worker when global worker execution is enabled.'
                                  )}
                                </FormDescription>
                              </div>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="exactIdNotFoundClassifierSafetyLagMinutes"
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t(
                                'admin.syncWorker.exactIdNotFoundClassifierSafetyLagMinutes',
                                'Not-found classifier safety lag (minutes)'
                              )}
                              help={t(
                                'admin.syncWorker.exactIdNotFoundClassifierSafetyLagMinutesDescription',
                                'Wait this many minutes after an Exact-ID not-found result before checking confirmed absence.'
                              )}
                            />
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                max={2_147_483_647}
                                aria-label={t(
                                  'admin.syncWorker.exactIdNotFoundClassifierSafetyLagMinutes',
                                  'Not-found classifier safety lag (minutes)'
                                )}
                                value={field.value}
                                onChange={(event) => {
                                  field.onChange(Number(event.target.value));
                                }}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="exactIdNotFoundClassifierCron"
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t(
                                'admin.syncWorker.exactIdNotFoundClassifierCron',
                                'Not-found classifier cadence'
                              )}
                              help={t(
                                'admin.syncWorker.exactIdNotFoundClassifierCronDescription',
                                'Cron schedule for the authenticated Exact-ID absence classifier.'
                              )}
                            />
                            <FormControl>
                              <Input
                                aria-label={t(
                                  'admin.syncWorker.exactIdNotFoundClassifierCron',
                                  'Not-found classifier cadence'
                                )}
                                value={field.value}
                                onChange={field.onChange}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="exactIdTerminalRetentionDays"
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t(
                                'admin.syncWorker.exactIdTerminalRetentionDays',
                                'Terminal retention days'
                              )}
                              help={t(
                                'admin.syncWorker.exactIdTerminalRetentionDaysDescription',
                                'How long synced, not-found, and terminal failed exact-ID rows stay available for review before cleanup.'
                              )}
                            />
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                max={30}
                                value={field.value}
                                onChange={(event) => {
                                  field.onChange(Number(event.target.value));
                                }}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                            {renderEffectiveRuntimeNote('exactIdTerminalRetentionDays', (value) =>
                              t('admin.syncWorker.daysLabel', {
                                count: Number(value),
                                defaultValue: '{{count}} days',
                              })
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="createUpdateWebhookInboxMaxRecordsPerRun"
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t(
                                'admin.syncWorker.createUpdateWebhookInboxMaxRecordsPerRun',
                                'Create/update inbox batch size'
                              )}
                              help={t(
                                'admin.syncWorker.createUpdateWebhookInboxMaxRecordsPerRunDescription',
                                'How many durable create_update_webhook inbox rows one webhook drain may hand off to exact-ID sync.'
                              )}
                            />
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                max={250}
                                value={field.value}
                                onChange={(event) => {
                                  field.onChange(Number(event.target.value));
                                }}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                            {renderEffectiveRuntimeNote(
                              'createUpdateWebhookInboxMaxRecordsPerRun',
                              (value) =>
                                t('admin.syncWorker.rowsLabel', {
                                  count: Number(value),
                                  defaultValue: '{{count}} rows',
                                })
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="deleteWebhookInboxBulkAckMinRecords"
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t(
                                'admin.syncWorker.deleteWebhookInboxBulkAckMinRecords',
                                'Delete inbox bulk ack threshold'
                              )}
                              help={t(
                                'admin.syncWorker.deleteWebhookInboxBulkAckMinRecordsDescription',
                                'Minimum delete_webhook ack batch size that uses the Bubble update_many_rows workflow instead of direct PATCH.'
                              )}
                            />
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                max={WEBHOOK_INBOX_BULK_ACK_MIN_RECORDS_MAX}
                                value={field.value}
                                onChange={(event) => {
                                  field.onChange(Number(event.target.value));
                                }}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                            {renderEffectiveRuntimeNote(
                              'deleteWebhookInboxBulkAckMinRecords',
                              (value) =>
                                t('admin.syncWorker.rowsLabel', {
                                  count: Number(value),
                                  defaultValue: '{{count}} rows',
                                })
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="createUpdateWebhookInboxBulkAckMinRecords"
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t(
                                'admin.syncWorker.createUpdateWebhookInboxBulkAckMinRecords',
                                'Create/update inbox bulk ack threshold'
                              )}
                              help={t(
                                'admin.syncWorker.createUpdateWebhookInboxBulkAckMinRecordsDescription',
                                'Minimum create_update_webhook ack batch size that uses the Bubble update_many_rows workflow instead of direct PATCH.'
                              )}
                            />
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                max={WEBHOOK_INBOX_BULK_ACK_MIN_RECORDS_MAX}
                                value={field.value}
                                onChange={(event) => {
                                  field.onChange(Number(event.target.value));
                                }}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                            {renderEffectiveRuntimeNote(
                              'createUpdateWebhookInboxBulkAckMinRecords',
                              (value) =>
                                t('admin.syncWorker.rowsLabel', {
                                  count: Number(value),
                                  defaultValue: '{{count}} rows',
                                })
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </Stack>
                  </RuntimeFieldCard>

                  <RuntimeFieldCard
                    className="lg:col-span-2"
                    title={t('admin.syncWorker.deltaSchedule', 'Delta import schedule')}
                    description={t(
                      'admin.syncWorker.deltaScheduleDescription',
                      'How often the system starts planned delta sync across enabled tables. This is shown as a global sync concept even though the schedule is owned internally by the scheduling runtime.'
                    )}
                  >
                    <FormField
                      control={form.control}
                      name="globalDeltaCron"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            {renderIntervalControls(
                              'global-delta',
                              globalDeltaInterval,
                              setGlobalDeltaInterval,
                              field.onChange
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </RuntimeFieldCard>
                </div>
              </TabsContent>

              <TabsContent value="media-import" className="mt-4">
                <div className="grid gap-4 lg:grid-cols-2">
                  <RuntimeFieldCard
                    title={t('admin.syncWorker.mediaImportControlTitle', 'Media scheduler control')}
                    description={t(
                      'admin.syncWorker.mediaImportControlDescription',
                      'Control the media importer and the number of files it processes at once.'
                    )}
                  >
                    <Stack gap="sm">
                      <FormField
                        control={form.control}
                        name="mediaImportSchedulerEnabled"
                        render={({ field }) => (
                          <FormItem className="flex items-center justify-between gap-4 rounded-lg border border-border p-3">
                            <div>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.mediaImportSchedulerEnabled',
                                  'Media scheduler enabled'
                                )}
                                help={t(
                                  'admin.syncWorker.mediaImportSchedulerEnabledDescription',
                                  'Turn off only the binary media import scheduler while keeping other worker maintenance jobs alive.'
                                )}
                              />
                              <FormDescription>
                                {t(
                                  'admin.syncWorker.mediaImportSchedulerEnabledFormDescription',
                                  'Emergency switch for media imports.'
                                )}
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="mediaImportSmallConcurrency"
                        rules={{
                          min: { value: 1, message: 'Concurrency must be at least 1.' },
                          max: { value: 50, message: 'Concurrency must be at most 50.' },
                        }}
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t(
                                'admin.syncWorker.mediaImportConcurrency',
                                'Parallel media imports'
                              )}
                              help={t(
                                'admin.syncWorker.mediaImportConcurrencyDescription',
                                'Maximum number of files imported at once across all workers, regardless of file size.'
                              )}
                            />
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                max={50}
                                step={1}
                                value={field.value}
                                onChange={(event) => {
                                  field.onChange(Number(event.target.value));
                                }}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                            {renderEffectiveRuntimeNote('mediaImportSmallConcurrency', (value) =>
                              String(value)
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Text size="sm" color="muted">
                        {t(
                          'admin.syncWorker.mediaImportInternalSettingsNotice',
                          'Legacy size-lane and preflight settings remain stored for compatibility but are not used by media imports.'
                        )}
                      </Text>
                    </Stack>
                  </RuntimeFieldCard>

                  <RuntimeFieldCard
                    title={t('admin.syncWorker.mediaOwnerSyncTitle', 'Owner discovery')}
                    description={t(
                      'admin.syncWorker.mediaOwnerSyncDescription',
                      'Operator-safe owner discovery control. Batch size, processing concurrency, lease caps, tick budget, and pressure thresholds remain DB-only engineering settings.'
                    )}
                  >
                    <Stack gap="sm">
                      <FormField
                        control={form.control}
                        name="mediaOwnerSyncSchedulerEnabled"
                        render={({ field }) => (
                          <FormItem className="flex items-center justify-between gap-4 rounded-lg border border-border p-3">
                            <div>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.mediaOwnerSyncSchedulerEnabled',
                                  'Owner discovery enabled'
                                )}
                                help={t(
                                  'admin.syncWorker.mediaOwnerSyncSchedulerEnabledDescription',
                                  'Separate kill switch for DB-heavy media owner discovery. Use it without pausing binary import settings.'
                                )}
                              />
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="mediaOwnerMissingReconcileIntervalMinutes"
                        rules={{
                          required: t(
                            'admin.syncWorker.mediaOwnerMissingReconcileIntervalRequired',
                            'Reconcile interval is required.'
                          ),
                          min: {
                            value: 1,
                            message: t(
                              'admin.syncWorker.mediaOwnerMissingReconcileIntervalMin',
                              'Reconcile interval must be at least 1 minute.'
                            ),
                          },
                          max: {
                            value: 35_791,
                            message: t(
                              'admin.syncWorker.mediaOwnerMissingReconcileIntervalMax',
                              'Reconcile interval exceeds the timer-safe maximum.'
                            ),
                          },
                        }}
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t(
                                'admin.syncWorker.mediaOwnerMissingReconcileInterval',
                                'Missing-owner reconcile interval (minutes)'
                              )}
                              help={t(
                                'admin.syncWorker.mediaOwnerMissingReconcileIntervalDescription',
                                'Database-owned cadence for bounded recovery of media rows whose owner queue row is missing.'
                              )}
                            />
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                max={35_791}
                                step={1}
                                value={field.value}
                                onChange={(event) => {
                                  field.onChange(Number(event.target.value));
                                }}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Text size="sm" color="muted">
                        {t(
                          'admin.syncWorker.mediaOwnerSyncInternalSettingsNotice',
                          'Owner discovery budgets are still stored in sync_worker_settings for emergency DB-side tuning, but they are not operator-facing form fields.'
                        )}
                      </Text>
                    </Stack>
                  </RuntimeFieldCard>
                </div>
              </TabsContent>

              <TabsContent value="relation-refresh" className="mt-4">
                <div className="grid gap-4 lg:grid-cols-2">
                  <RuntimeFieldCard
                    title={t(
                      'admin.syncWorker.relationRefreshScheduleTitle',
                      'Regular Relation Refresh'
                    )}
                    description={t(
                      'admin.syncWorker.relationRefreshScheduleDescription',
                      'Controls when deferred relation refresh runs and how large each run can be.'
                    )}
                  >
                    <FormField
                      control={form.control}
                      name="relationRefreshEnabled"
                      render={({ field }) => (
                        <FormItem className="mb-4 rounded-md border border-border p-3">
                          <div className="flex items-center justify-between gap-4">
                            <div className="min-w-0">
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.relationRefreshEnabled',
                                  'Enable regular relation refresh'
                                )}
                                help={t(
                                  'admin.syncWorker.relationRefreshEnabledDescription',
                                  'Processes the regular relation refresh backlog independently from bulk catch-up work.'
                                )}
                              />
                              <FormDescription>
                                {t(
                                  'admin.syncWorker.relationRefreshEnabledDescription',
                                  'Processes the regular relation refresh backlog independently from bulk catch-up work.'
                                )}
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={isReadOnly || isLoading || !workerSyncEnabled}
                              />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="relationRefreshCron"
                      render={({ field }) => (
                        <FormItem>
                          <SettingLabel
                            label={t(
                              'admin.syncWorker.relationRefreshCron',
                              'Relation Refresh Schedule'
                            )}
                            help={t(
                              'admin.syncWorker.relationRefreshCronDescription',
                              'How often deferred relation refresh starts.'
                            )}
                          />
                          <FormControl>
                            {renderIntervalControls(
                              'relation-refresh',
                              relationRefreshInterval,
                              setRelationRefreshInterval,
                              field.onChange
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="relationRefreshBatchLimit"
                      render={({ field }) => (
                        <FormItem>
                          <SettingLabel
                            label={t(
                              'admin.syncWorker.relationRefreshBatchLimit',
                              'Relation Refresh Batch Limit'
                            )}
                            help={t(
                              'admin.syncWorker.relationRefreshBatchLimitDescription',
                              'Maximum number of owner rows claimed per relation-refresh run.'
                            )}
                          />
                          <FormControl>
                            <Input
                              type="number"
                              min={1}
                              value={field.value}
                              onChange={(event) => {
                                field.onChange(Number(event.target.value));
                              }}
                              disabled={isReadOnly || isLoading}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="relationRefreshLeaseMs"
                      render={({ field }) => (
                        <FormItem>
                          <SettingLabel
                            label={t(
                              'admin.syncWorker.relationRefreshLeaseMs',
                              'Relation Refresh Lease'
                            )}
                            help={t(
                              'admin.syncWorker.relationRefreshLeaseMsDescription',
                              'How long a claimed relation-refresh row stays leased to one worker.'
                            )}
                          />
                          <FormControl>
                            <Input
                              type="number"
                              min={1}
                              value={field.value}
                              onChange={(event) => {
                                field.onChange(Number(event.target.value));
                              }}
                              disabled={isReadOnly || isLoading}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </RuntimeFieldCard>

                  <RuntimeFieldCard
                    title={t('admin.syncWorker.relationRefreshBulkTitle', 'Bulk Relation Refresh')}
                    description={t(
                      'admin.syncWorker.relationRefreshBulkDescription',
                      'Fast bounded lane for catch-up work. It uses per-table policy switches and keeps leases short so the regular worker remains safe.'
                    )}
                  >
                    <Stack gap="md">
                      <FormField
                        control={form.control}
                        name="relationRefreshBulkEnabled"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex items-center justify-between gap-4">
                              <div className="min-w-0">
                                <SettingLabel
                                  label={t(
                                    'admin.syncWorker.relationRefreshBulkEnabled',
                                    'Enable bulk relation refresh'
                                  )}
                                  help={t(
                                    'admin.syncWorker.relationRefreshBulkEnabledDescription',
                                    'Turns on the accelerated relation-refresh lane without changing per-table eligibility.'
                                  )}
                                />
                                <FormDescription>
                                  {t(
                                    'admin.syncWorker.relationRefreshBulkEnabledDescription',
                                    'Turns on the accelerated relation-refresh lane without changing per-table eligibility.'
                                  )}
                                </FormDescription>
                              </div>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  disabled={isReadOnly || isLoading || !workerSyncEnabled}
                                />
                              </FormControl>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="relationRefreshBulkCron"
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t(
                                'admin.syncWorker.relationRefreshBulkCron',
                                'Bulk Relation Refresh Schedule'
                              )}
                              help={t(
                                'admin.syncWorker.relationRefreshBulkCronDescription',
                                'How often the worker attempts a bounded bulk catch-up pass.'
                              )}
                            />
                            <FormControl>
                              {renderIntervalControls(
                                'relation-refresh-bulk',
                                relationRefreshBulkInterval,
                                setRelationRefreshBulkInterval,
                                field.onChange
                              )}
                            </FormControl>
                            {renderEffectiveRuntimeNote('relationRefreshBulkCron', (value) =>
                              formatCronLabel(String(value))
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid gap-4 sm:grid-cols-3">
                        <FormField
                          control={form.control}
                          name="relationRefreshBulkMicrobatchSize"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.relationRefreshBulkMicrobatchSize',
                                  'Microbatch Size'
                                )}
                                help={t(
                                  'admin.syncWorker.relationRefreshBulkMicrobatchSizeDescription',
                                  'Maximum relation-refresh rows claimed in one small bulk chunk.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  max={RELATION_REFRESH_BULK_MICROBATCH_SIZE_MAX}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(Number(event.target.value));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              {renderEffectiveRuntimeNote(
                                'relationRefreshBulkMicrobatchSize',
                                (value) => String(value)
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="relationRefreshBulkTimeBudgetMs"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.relationRefreshBulkTimeBudgetMs',
                                  'Time Budget'
                                )}
                                help={t(
                                  'admin.syncWorker.relationRefreshBulkTimeBudgetMsDescription',
                                  'Maximum wall-clock time for one bulk pass before yielding.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(Number(event.target.value));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              {renderEffectiveRuntimeNote(
                                'relationRefreshBulkTimeBudgetMs',
                                (value) => formatMillisecondsLabel(Number(value))
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="relationRefreshBulkLeaseMs"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.relationRefreshBulkLeaseMs',
                                  'Bulk Lease'
                                )}
                                help={t(
                                  'admin.syncWorker.relationRefreshBulkLeaseMsDescription',
                                  'How long a bulk-claimed row stays leased before another worker may retry it.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(Number(event.target.value));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              {renderEffectiveRuntimeNote('relationRefreshBulkLeaseMs', (value) =>
                                formatMillisecondsLabel(Number(value))
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </Stack>
                  </RuntimeFieldCard>

                  <RuntimeFieldCard
                    title={t('admin.syncWorker.relationDeadRepairTitle', 'Dead Relation Repair')}
                    description={t(
                      'admin.syncWorker.relationDeadRepairDescription',
                      'Parent-first repair for rows already dead in sync_relation_refresh_queue. Dry-run stores compact repair plans before write-back is enabled.'
                    )}
                  >
                    <Stack gap="md">
                      <FormField
                        control={form.control}
                        name="relationDeadRepairEnabled"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex items-center justify-between gap-4">
                              <div className="min-w-0">
                                <SettingLabel
                                  label={t(
                                    'admin.syncWorker.relationDeadRepairEnabled',
                                    'Enable dead relation repair'
                                  )}
                                  help={t(
                                    'admin.syncWorker.relationDeadRepairEnabledDescription',
                                    'Allows the worker to inspect dead relation-refresh rows when global worker execution is enabled.'
                                  )}
                                />
                              </div>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  disabled={isReadOnly || isLoading || !workerSyncEnabled}
                                />
                              </FormControl>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid gap-4 md:grid-cols-2">
                        <FormField
                          control={form.control}
                          name="relationDeadRepairRootCron"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.relationDeadRepairRootCron',
                                  'Root cron'
                                )}
                                help={t(
                                  'admin.syncWorker.relationDeadRepairRootCronDescription',
                                  'Cron cadence for the safety root that starts Dead Repair.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  value={field.value}
                                  onChange={field.onChange}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              {renderEffectiveRuntimeNote('relationDeadRepairRootCron', String)}
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="relationDeadRepairContinuationDelayMs"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.relationDeadRepairContinuationDelayMs',
                                  'Continuation pause'
                                )}
                                help={t(
                                  'admin.syncWorker.relationDeadRepairContinuationDelayMsDescription',
                                  'Minimum pause before a bounded Dead Repair continuation.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  max={600000}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(Number(event.target.value));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              {renderEffectiveRuntimeNote(
                                'relationDeadRepairContinuationDelayMs',
                                (value) => formatMillisecondsLabel(Number(value))
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <FormField
                        control={form.control}
                        name="dataValidationScheduleEnabled"
                        render={({ field }) => (
                          <FormItem className="rounded-md border border-border p-3">
                            <div className="flex items-center justify-between gap-4">
                              <div className="min-w-0">
                                <SettingLabel
                                  label={t(
                                    'admin.syncWorker.dataValidationScheduleEnabled',
                                    'Enable scheduled monthly validation'
                                  )}
                                  help={t(
                                    'admin.syncWorker.dataValidationScheduleEnabledDescription',
                                    'Runs the monthly 30-day parity campaign from DB settings without a deploy.'
                                  )}
                                />
                                <FormDescription>
                                  {t(
                                    'admin.syncWorker.dataValidationScheduleEnabledDescription',
                                    'Runs the monthly 30-day parity campaign from DB settings without a deploy.'
                                  )}
                                </FormDescription>
                              </div>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  disabled={isReadOnly || isLoading || !workerSyncEnabled}
                                />
                              </FormControl>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                        <FormField
                          control={form.control}
                          name="dataValidationScheduleCron"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.dataValidationScheduleCron',
                                  'Monthly schedule'
                                )}
                                help={t(
                                  'admin.syncWorker.dataValidationScheduleCronDescription',
                                  'Cron expression for the scheduled monthly campaign.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  value={field.value}
                                  onChange={field.onChange}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="dataValidationLookbackDays"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.dataValidationLookbackDays',
                                  'Lookback days'
                                )}
                                help={t(
                                  'admin.syncWorker.dataValidationLookbackDaysDescription',
                                  'Closed monthly campaign window length.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(Number(event.target.value));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="dataValidationSafetyLagDays"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.dataValidationSafetyLagDays',
                                  'Safety lag days'
                                )}
                                help={t(
                                  'admin.syncWorker.dataValidationSafetyLagDaysDescription',
                                  'Keeps the newest changing records out of the monthly window.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={0}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(Number(event.target.value));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="dataValidationRepairWaitMinutes"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.dataValidationRepairWaitMinutes',
                                  'Repair wait minutes'
                                )}
                                help={t(
                                  'admin.syncWorker.dataValidationRepairWaitMinutesDescription',
                                  'Bounded wait before dirty rows are rechecked after repair queues run.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(Number(event.target.value));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid gap-4 sm:grid-cols-3">
                        <FormField
                          control={form.control}
                          name="dataValidationMaxRowsPerTable"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.dataValidationMaxRowsPerTable',
                                  'Max rows/table'
                                )}
                                help={t(
                                  'admin.syncWorker.dataValidationMaxRowsPerTableDescription',
                                  'Upper bound for each monthly table slice.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(Number(event.target.value));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="dataValidationDirtyRecheckAttempts"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.dataValidationDirtyRecheckAttempts',
                                  'Dirty recheck attempts'
                                )}
                                help={t(
                                  'admin.syncWorker.dataValidationDirtyRecheckAttemptsDescription',
                                  'Maximum recheck attempts for records repaired after initial findings.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(Number(event.target.value));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="dataValidationMaxTablesPerCampaign"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.dataValidationMaxTablesPerCampaign',
                                  'Max tables/campaign'
                                )}
                                help={t(
                                  'admin.syncWorker.dataValidationMaxTablesPerCampaignDescription',
                                  'Caps how many inventory tables one monthly campaign validates.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(Number(event.target.value));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {(
                          [
                            ['syncTableQueueTotalExecutionsLimit', 'Table queue executions'],
                            ['syncExactIdQueueTotalExecutionsLimit', 'Exact-ID queue executions'],
                            [
                              'relationRefreshQueueTotalExecutionsLimit',
                              'Relation refresh executions',
                            ],
                            ['mediaOwnerSyncQueueTotalExecutionsLimit', 'Media owner executions'],
                            ['mediaImportQueueTotalExecutionsLimit', 'Media import executions'],
                          ] as const
                        ).map(([name, label]) => (
                          <FormField
                            key={name}
                            control={form.control}
                            name={name}
                            render={({ field }) => (
                              <FormItem>
                                <SettingLabel
                                  label={t(`admin.syncWorker.${name}`, label)}
                                  help={t(
                                    `admin.syncWorker.${name}Description`,
                                    'Total application executions allowed before a retryable row becomes terminal.'
                                  )}
                                />
                                <FormControl>
                                  <Input
                                    type="number"
                                    min={1}
                                    step={1}
                                    value={field.value}
                                    onChange={(event) => {
                                      field.onChange(Number(event.target.value));
                                    }}
                                    disabled={isReadOnly || isLoading}
                                  />
                                </FormControl>
                                {renderEffectiveRuntimeNote(name, (value) => String(value))}
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        ))}
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <FormField
                          control={form.control}
                          name="relationDeadRepairBatchSize"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.relationDeadRepairBatchSize',
                                  'Batch size'
                                )}
                                help={t(
                                  'admin.syncWorker.relationDeadRepairBatchSizeDescription',
                                  'Maximum dead relation rows claimed by one repair pass.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  max={RELATION_DEAD_REPAIR_BATCH_SIZE_MAX}
                                  value={field.value}
                                  onChange={(event) => {
                                    const requested = Number(event.target.value);
                                    if (!Number.isFinite(requested)) return;
                                    field.onChange(
                                      Math.max(
                                        1,
                                        Math.min(requested, RELATION_DEAD_REPAIR_BATCH_SIZE_MAX)
                                      )
                                    );
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              {renderEffectiveRuntimeNote('relationDeadRepairBatchSize', (value) =>
                                String(value)
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="relationDeadRepairTimeBudgetMs"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.relationDeadRepairTimeBudgetMs',
                                  'Time budget'
                                )}
                                help={t(
                                  'admin.syncWorker.relationDeadRepairTimeBudgetMsDescription',
                                  'Maximum wall-clock time for one repair pass before yielding.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  max={120000}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(Number(event.target.value));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              {renderEffectiveRuntimeNote(
                                'relationDeadRepairTimeBudgetMs',
                                (value) => formatMillisecondsLabel(Number(value))
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="relationDeadRepairBubbleRateLimitPerMinute"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.relationDeadRepairBubbleRateLimitPerMinute',
                                  'Bubble calls per minute'
                                )}
                                help={t(
                                  'admin.syncWorker.relationDeadRepairBubbleRateLimitPerMinuteDescription',
                                  'Upper bound for Bubble reads and write-back calls during repair.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  max={300}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(Number(event.target.value));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              {renderEffectiveRuntimeNote(
                                'relationDeadRepairBubbleRateLimitPerMinute',
                                (value) => String(value)
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="relationDeadRepairLeaseMs"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.relationDeadRepairLeaseMs',
                                  'Lease window'
                                )}
                                help={t(
                                  'admin.syncWorker.relationDeadRepairLeaseMsDescription',
                                  'How long one worker owns a dead-repair row before it can be recovered.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={10000}
                                  max={600000}
                                  step={1000}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(Number(event.target.value));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              {renderEffectiveRuntimeNote('relationDeadRepairLeaseMs', (value) =>
                                formatMillisecondsLabel(Number(value))
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="relationDeadRepairMaxAttempts"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.relationDeadRepairMaxAttempts',
                                  'Max attempts'
                                )}
                                help={t(
                                  'admin.syncWorker.relationDeadRepairMaxAttemptsDescription',
                                  'Maximum repair attempts before a dead row stays unsafe for manual review.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  max={20}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(Number(event.target.value));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              {renderEffectiveRuntimeNote(
                                'relationDeadRepairMaxAttempts',
                                (value) => String(value)
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="relationDeadRepairRetentionDays"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.relationDeadRepairRetentionDays',
                                  'Plan retention days'
                                )}
                                help={t(
                                  'admin.syncWorker.relationDeadRepairRetentionDaysDescription',
                                  'How long completed compact repair summaries stay on queue rows for review.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  max={30}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(Number(event.target.value));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              {renderEffectiveRuntimeNote(
                                'relationDeadRepairRetentionDays',
                                (value) =>
                                  t('admin.syncWorker.daysLabel', {
                                    count: Number(value),
                                    defaultValue: '{{count}} days',
                                  })
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </Stack>
                  </RuntimeFieldCard>
                </div>
              </TabsContent>

              <TabsContent value="delete-lane" className="mt-4">
                <div className="grid gap-4 lg:grid-cols-2">
                  <RuntimeFieldCard
                    title={t('admin.syncWorker.deleteLanePrimaryTitle', 'Delete Lane')}
                    description={t(
                      'admin.syncWorker.deleteLanePrimaryDescription',
                      'Controls whether verified delete cleanup runs and how often the backlog is checked.'
                    )}
                  >
                    <div className="grid gap-4">
                      <FormField
                        control={form.control}
                        name="deleteExecutionEnabled"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex items-center justify-between gap-4">
                              <div className="min-w-0">
                                <SettingLabel
                                  label={t(
                                    'admin.syncWorker.deleteExecutionEnabled',
                                    'Enable delete processing'
                                  )}
                                  help={t(
                                    'admin.syncWorker.deleteExecutionEnabledDescription',
                                    'Turns the verified delete backlog worker on or off without changing the stored cadence and retry settings.'
                                  )}
                                />
                                <FormDescription>
                                  {t(
                                    'admin.syncWorker.deleteExecutionEnabledDescription',
                                    'Turns the verified delete backlog worker on or off without changing the stored cadence and retry settings.'
                                  )}
                                </FormDescription>
                              </div>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="deleteBacklogCron"
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t(
                                'admin.syncWorker.deleteBacklogCron',
                                'How often to check delete backlog'
                              )}
                              help={t(
                                'admin.syncWorker.deleteBacklogCronDescription',
                                'Human-readable cadence for verified delete backlog scans. Includes a 1-minute option for the fastest supported check loop.'
                              )}
                            />
                            <FormControl>
                              {renderIntervalControls(
                                'delete-backlog',
                                deleteBacklogInterval,
                                setDeleteBacklogInterval,
                                field.onChange
                              )}
                            </FormControl>
                            {renderEffectiveRuntimeNote('deleteBacklogCron', (value) =>
                              formatCronLabel(String(value))
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="deleteBacklogMaxAttempts"
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t(
                                'admin.syncWorker.deleteBacklogMaxAttempts',
                                'How many retries before the row stops retrying'
                              )}
                              help={t(
                                'admin.syncWorker.deleteBacklogMaxAttemptsDescription',
                                'Maximum verified delete attempts before the backlog row stops retrying automatically.'
                              )}
                            />
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                value={field.value}
                                onChange={(event) => {
                                  field.onChange(Number(event.target.value));
                                }}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                            {renderEffectiveRuntimeNote('deleteBacklogMaxAttempts', (value) =>
                              String(value)
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="deleteBacklogMaxRowsPerRun"
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t(
                                'admin.syncWorker.deleteBacklogMaxRowsPerRun',
                                'Rows per worker run'
                              )}
                              help={t(
                                'admin.syncWorker.deleteBacklogMaxRowsPerRunDescription',
                                'Maximum delete backlog rows one worker invocation may process before yielding.'
                              )}
                            />
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                value={field.value}
                                onChange={(event) => {
                                  field.onChange(Number(event.target.value));
                                }}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                            {renderEffectiveRuntimeNote('deleteBacklogMaxRowsPerRun', (value) =>
                              String(value)
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </RuntimeFieldCard>

                  <RuntimeFieldCard
                    title={t(
                      'admin.syncWorker.deletedRelationCleanupTitle',
                      'Deleted Relation Cleanup'
                    )}
                    description={t(
                      'admin.syncWorker.deletedRelationCleanupDescription',
                      'Schedules stale parent-reference cleanup after a verified delete. Dry-run records compact queue summaries before write-back is enabled.'
                    )}
                  >
                    <div className="grid gap-4">
                      <FormField
                        control={form.control}
                        name="deletedRelationCleanupEnabled"
                        render={({ field }) => (
                          <FormItem>
                            <div className="flex items-center justify-between gap-4">
                              <div className="min-w-0">
                                <SettingLabel
                                  label={t(
                                    'admin.syncWorker.deletedRelationCleanupEnabled',
                                    'Enable deleted relation cleanup'
                                  )}
                                  help={t(
                                    'admin.syncWorker.deletedRelationCleanupEnabledDescription',
                                    'Allows the cleanup worker to process rows from sync_deleted_relation_cleanup_queue. Global Worker Sync Enabled still overrides it.'
                                  )}
                                />
                                <FormDescription>
                                  {t(
                                    'admin.syncWorker.deletedRelationCleanupEnabledDescription',
                                    'Allows the cleanup worker to process rows from sync_deleted_relation_cleanup_queue. Global Worker Sync Enabled still overrides it.'
                                  )}
                                </FormDescription>
                              </div>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid gap-4 sm:grid-cols-2">
                        <FormField
                          control={form.control}
                          name="deletedRelationCleanupBatchSize"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.deletedRelationCleanupBatchSize',
                                  'Batch size'
                                )}
                                help={t(
                                  'admin.syncWorker.deletedRelationCleanupBatchSizeDescription',
                                  'Maximum cleanup rows claimed by one worker pass.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  max={100}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(Number(event.target.value));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              {renderEffectiveRuntimeNote(
                                'deletedRelationCleanupBatchSize',
                                (value) => String(value)
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="deletedRelationCleanupRateLimitPerMinute"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.deletedRelationCleanupRateLimitPerMinute',
                                  'Bubble calls per minute'
                                )}
                                help={t(
                                  'admin.syncWorker.deletedRelationCleanupRateLimitPerMinuteDescription',
                                  'Upper bound for Bubble write-back calls when write-back mode is active.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  max={300}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(Number(event.target.value));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              {renderEffectiveRuntimeNote(
                                'deletedRelationCleanupRateLimitPerMinute',
                                (value) => String(value)
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </RuntimeFieldCard>
                </div>
              </TabsContent>

              <TabsContent value="maintenance" className="mt-4">
                <div className="grid gap-4 lg:grid-cols-2">
                  <RuntimeFieldCard
                    className="lg:col-span-2"
                    title={t(
                      'admin.syncWorker.maintenanceCleanupTitle',
                      'Technical-history maintenance'
                    )}
                    description={t(
                      'admin.syncWorker.maintenanceCleanupDescription',
                      'The worker evaluates the database-owned cadence and runs at most one bounded batch for each family. A successful webhook receipt group is always deleted atomically, even when one group exceeds its usual budget.'
                    )}
                  >
                    <Stack gap="md">
                      <FormField
                        control={form.control}
                        name="retentionCleanupIntervalMinutes"
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t(
                                'admin.syncWorker.retentionCleanupIntervalMinutes',
                                'Maintenance interval (minutes)'
                              )}
                              help={t(
                                'admin.syncWorker.retentionCleanupIntervalMinutesDescription',
                                'How often the worker may begin the next cleanup pass. The default is 30 minutes.'
                              )}
                            />
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                max={2_147_483_647}
                                value={field.value}
                                onChange={(event) => {
                                  field.onChange(Number(event.target.value));
                                }}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                            {renderEffectiveRuntimeNote(
                              'retentionCleanupIntervalMinutes',
                              (value) =>
                                t('admin.syncWorker.minutesLabel', {
                                  count: Number(value),
                                  defaultValue: '{{count}} minutes',
                                })
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <Text size="sm" color="muted">
                        {t(
                          'admin.syncWorker.maintenanceCleanupBatchNotice',
                          'Every positive value is a per-family target, not a drain loop. Successful outbound deletion remains disabled until the separate cutover has completed and been verified.'
                        )}
                      </Text>

                      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {MAINTENANCE_CLEANUP_BATCH_FIELDS.map((maintenanceField) => (
                          <FormField
                            key={maintenanceField.field}
                            control={form.control}
                            name={maintenanceField.field}
                            render={({ field }) => (
                              <FormItem>
                                <SettingLabel
                                  label={maintenanceField.label}
                                  help={maintenanceField.help}
                                />
                                <FormControl>
                                  <Input
                                    type="number"
                                    min={1}
                                    max={2_147_483_647}
                                    value={field.value}
                                    onChange={(event) => {
                                      field.onChange(Number(event.target.value));
                                    }}
                                    disabled={isReadOnly || isLoading}
                                  />
                                </FormControl>
                                {renderEffectiveRuntimeNote(maintenanceField.field, (value) =>
                                  t('admin.syncWorker.rowsLabel', {
                                    count: Number(value),
                                    defaultValue: '{{count}} rows',
                                  })
                                )}
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        ))}
                      </div>
                    </Stack>
                  </RuntimeFieldCard>
                </div>
              </TabsContent>

              <TabsContent value="snapshot-lane" className="mt-4">
                <Stack gap="md">
                  {renderSnapshotExecutionNotice()}

                  <div className="grid gap-4 lg:grid-cols-2">
                    <RuntimeFieldCard
                      title={t('admin.syncWorker.snapshotLanePrimaryTitle', 'Snapshot Lane')}
                      description={t(
                        'admin.syncWorker.snapshotLanePrimaryDescription',
                        'Controls whether snapshot refresh runs and how quickly each snapshot family is expected to stay fresh.'
                      )}
                    >
                      <div className="grid gap-4">
                        <FormField
                          control={form.control}
                          name="snapshotExecutionEnabled"
                          render={({ field }) => (
                            <FormItem>
                              <div className="flex items-center justify-between gap-4">
                                <div className="min-w-0">
                                  <SettingLabel
                                    label={t(
                                      'admin.syncWorker.snapshotExecutionEnabled',
                                      'Enable snapshot refresh'
                                    )}
                                    help={t(
                                      'admin.syncWorker.snapshotExecutionEnabledDescription',
                                      'Nested snapshot-lane switch. Global Worker Sync Enabled still overrides it.'
                                    )}
                                  />
                                  <FormDescription>
                                    {t(
                                      'admin.syncWorker.snapshotExecutionEnabledDescription',
                                      'Nested snapshot-lane switch. Global Worker Sync Enabled still overrides it.'
                                    )}
                                  </FormDescription>
                                </div>
                                <FormControl>
                                  <Switch
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                    disabled={isReadOnly || isLoading}
                                  />
                                </FormControl>
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="snapshotSupervisorIntervalSeconds"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.snapshotSupervisorIntervalSeconds',
                                  'How often to refresh snapshots'
                                )}
                                help={t(
                                  'admin.syncWorker.snapshotSupervisorIntervalSecondsDescription',
                                  'How often the control plane checks snapshot freshness and enqueues the next snapshot refresh work.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(Number(event.target.value));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              <FormDescription>
                                {t('admin.syncWorker.snapshotSupervisorIntervalSecondsUnits', {
                                  defaultValue: 'Requested runtime: {{value}}',
                                  value: formatSecondsLabel(field.value),
                                })}
                              </FormDescription>
                              {renderEffectiveRuntimeNote(
                                'snapshotSupervisorIntervalSeconds',
                                (value) => formatSecondsLabel(Number(value))
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="snapshotJobsFreshnessSlaSeconds"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.snapshotJobsFreshnessSlaSeconds',
                                  'When jobs snapshot becomes stale'
                                )}
                                help={t(
                                  'admin.syncWorker.snapshotJobsFreshnessSlaSecondsDescription',
                                  'Expected freshness window for `/en/sync/jobs` snapshot data.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(Number(event.target.value));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              {renderEffectiveRuntimeNote(
                                'snapshotJobsFreshnessSlaSeconds',
                                (value) => formatSecondsLabel(Number(value))
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="snapshotDataTypesFreshnessSlaSeconds"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.snapshotDataTypesFreshnessSlaSeconds',
                                  'When data types snapshot becomes stale'
                                )}
                                help={t(
                                  'admin.syncWorker.snapshotDataTypesFreshnessSlaSecondsDescription',
                                  'Expected freshness window for `/en/sync/data-types` snapshot data.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(Number(event.target.value));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              {renderEffectiveRuntimeNote(
                                'snapshotDataTypesFreshnessSlaSeconds',
                                (value) => formatSecondsLabel(Number(value))
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </RuntimeFieldCard>

                    <RuntimeFieldCard
                      title={t(
                        'admin.syncWorker.relationSnapshotExactRecountTitle',
                        'Relation Queue Snapshot Repair'
                      )}
                      description={t(
                        'admin.syncWorker.relationSnapshotExactRecountDescription',
                        'Slow exact recount is isolated from relation building. Hot worker transitions update counters with cheap deltas; this background pass repairs drift on a bounded schedule.'
                      )}
                    >
                      <Stack gap="md">
                        <FormField
                          control={form.control}
                          name="relationSnapshotExactRecountEnabled"
                          render={({ field }) => (
                            <FormItem>
                              <div className="flex items-center justify-between gap-4">
                                <div className="min-w-0">
                                  <SettingLabel
                                    label={t(
                                      'admin.syncWorker.relationSnapshotExactRecountEnabled',
                                      'Enable exact recount'
                                    )}
                                    help={t(
                                      'admin.syncWorker.relationSnapshotExactRecountEnabledDescription',
                                      'Runs the slow exact snapshot repair on the snapshot worker lane instead of inside relation-refresh claim/process paths.'
                                    )}
                                  />
                                  <FormDescription>
                                    {t(
                                      'admin.syncWorker.relationSnapshotExactRecountEnabledDescription',
                                      'Runs the slow exact snapshot repair on the snapshot worker lane instead of inside relation-refresh claim/process paths.'
                                    )}
                                  </FormDescription>
                                </div>
                                <FormControl>
                                  <Switch
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                    disabled={isReadOnly || isLoading}
                                  />
                                </FormControl>
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="relationSnapshotExactRecountCron"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.relationSnapshotExactRecountCron',
                                  'Exact recount schedule'
                                )}
                                help={t(
                                  'admin.syncWorker.relationSnapshotExactRecountCronDescription',
                                  'How often the snapshot worker performs bounded exact recount repair for relation queue counters.'
                                )}
                              />
                              <FormControl>
                                {renderIntervalControls(
                                  'relation-snapshot-exact-recount',
                                  relationSnapshotExactRecountInterval,
                                  setRelationSnapshotExactRecountInterval,
                                  field.onChange
                                )}
                              </FormControl>
                              {renderEffectiveRuntimeNote(
                                'relationSnapshotExactRecountCron',
                                (value) => formatCronLabel(String(value))
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <div className="grid gap-4 sm:grid-cols-2">
                          <FormField
                            control={form.control}
                            name="relationSnapshotExactRecountMaxTablesPerRun"
                            render={({ field }) => (
                              <FormItem>
                                <SettingLabel
                                  label={t(
                                    'admin.syncWorker.relationSnapshotExactRecountMaxTablesPerRun',
                                    'Tables per run'
                                  )}
                                  help={t(
                                    'admin.syncWorker.relationSnapshotExactRecountMaxTablesPerRunDescription',
                                    'Maximum owner tables repaired by one exact recount job so one massive table cannot monopolize the worker.'
                                  )}
                                />
                                <FormControl>
                                  <Input
                                    type="number"
                                    min={1}
                                    max={100}
                                    value={field.value}
                                    onChange={(event) => {
                                      field.onChange(Number(event.target.value));
                                    }}
                                    disabled={isReadOnly || isLoading}
                                  />
                                </FormControl>
                                {renderEffectiveRuntimeNote(
                                  'relationSnapshotExactRecountMaxTablesPerRun',
                                  (value) => String(value)
                                )}
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </Stack>
                    </RuntimeFieldCard>
                  </div>
                </Stack>
              </TabsContent>

              <TabsContent value="analytics-gear" className="mt-4">
                <div className="grid gap-4 lg:grid-cols-2">
                  <RuntimeFieldCard
                    title={t('admin.syncWorker.analyticsGearLaneTitle', 'Analytics Gear lane')}
                    description={t(
                      'admin.syncWorker.analyticsGearLaneDescription',
                      'Controls Analytics Gear background jobs and manual recompute actions.'
                    )}
                  >
                    <FormField
                      control={form.control}
                      name="analyticsGearEnabled"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center justify-between gap-4">
                            <div className="min-w-0">
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.analyticsGearEnabled',
                                  'Enable Analytics Gear'
                                )}
                                help={t(
                                  'admin.syncWorker.analyticsGearEnabledDescription',
                                  'Controls Analytics Gear background calculations and manual recompute actions.'
                                )}
                              />
                              <FormDescription>
                                {t(
                                  'admin.syncWorker.analyticsGearEnabledDescription',
                                  'Controls Analytics Gear background calculations and manual recompute actions.'
                                )}
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={isReadOnly || isLoading || !workerSyncEnabled}
                              />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </RuntimeFieldCard>
                </div>
              </TabsContent>

              <TabsContent value="validation" className="mt-4">
                <div className="grid gap-4 lg:grid-cols-2">
                  <RuntimeFieldCard
                    title={t(
                      'admin.syncWorker.columnParityRefreshTitle',
                      'Column Schema Validation'
                    )}
                    description={t(
                      'admin.syncWorker.columnParityRefreshDescription',
                      'How often the worker checks contract columns in Postgres and stores missing-column diagnostics for /sync/data-types.'
                    )}
                  >
                    <FormField
                      control={form.control}
                      name="columnParityRefreshEnabled"
                      render={({ field }) => (
                        <FormItem className="mb-4 rounded-md border border-border p-3">
                          <div className="flex items-center justify-between gap-4">
                            <div className="min-w-0">
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.columnParityRefreshEnabled',
                                  'Enable column schema validation'
                                )}
                                help={t(
                                  'admin.syncWorker.columnParityRefreshEnabledDescription',
                                  'Keeps the Columns parity counts and drift popup diagnostics fresh for data types.'
                                )}
                              />
                              <FormDescription>
                                {t(
                                  'admin.syncWorker.columnParityRefreshEnabledDescription',
                                  'Keeps the Columns parity counts and drift popup diagnostics fresh for data types.'
                                )}
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={isReadOnly || isLoading || !workerSyncEnabled}
                              />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="columnParityRefreshCron"
                      render={({ field }) => (
                        <FormItem className="mb-4">
                          <SettingLabel
                            label={t(
                              'admin.syncWorker.columnParityRefreshCron',
                              'Column schema validation schedule'
                            )}
                            help={t(
                              'admin.syncWorker.columnParityRefreshCronDescription',
                              'How often all data types are rechecked against their mapping contracts and Postgres columns.'
                            )}
                          />
                          <FormControl>
                            {renderIntervalControls(
                              'column-parity-refresh',
                              columnParityInterval,
                              setColumnParityInterval,
                              field.onChange
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="grid gap-4 md:grid-cols-3">
                      <FormField
                        control={form.control}
                        name="columnParityRefreshMaxAttempts"
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t(
                                'admin.syncWorker.columnParityRefreshMaxAttempts',
                                'Max attempts'
                              )}
                              help={t(
                                'admin.syncWorker.columnParityRefreshMaxAttemptsDescription',
                                'Maximum retries before a failed table refresh waits for the next scheduled pass.'
                              )}
                            />
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                value={field.value}
                                onChange={(event) => {
                                  field.onChange(Number(event.target.value));
                                }}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="columnParityRefreshBaseBackoffSeconds"
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t(
                                'admin.syncWorker.columnParityRefreshBaseBackoffSeconds',
                                'Base backoff'
                              )}
                              help={t(
                                'admin.syncWorker.columnParityRefreshBaseBackoffSecondsDescription',
                                'Initial retry delay in seconds after a failed column schema refresh.'
                              )}
                            />
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                value={field.value}
                                onChange={(event) => {
                                  field.onChange(Number(event.target.value));
                                }}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="columnParityRefreshMaxBackoffSeconds"
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t(
                                'admin.syncWorker.columnParityRefreshMaxBackoffSeconds',
                                'Max backoff'
                              )}
                              help={t(
                                'admin.syncWorker.columnParityRefreshMaxBackoffSecondsDescription',
                                'Largest retry delay in seconds. Backend validation keeps it greater than or equal to base backoff.'
                              )}
                            />
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                value={field.value}
                                onChange={(event) => {
                                  field.onChange(Number(event.target.value));
                                }}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </RuntimeFieldCard>

                  <RuntimeFieldCard
                    title={t(
                      'admin.syncWorker.dataValidationSettingsTitle',
                      'Data Parity Validation'
                    )}
                    description={t(
                      'admin.syncWorker.dataValidationSettingsDescription',
                      'Operator-safe settings for the Bubble/Postgres parity validator. Storage, cleanup, lease, and hard full-scan internals stay backend-owned.'
                    )}
                  >
                    <Stack gap="md">
                      <FormField
                        control={form.control}
                        name="dataValidationEnabled"
                        render={({ field }) => (
                          <FormItem className="rounded-md border border-border p-3">
                            <div className="flex items-center justify-between gap-4">
                              <div className="min-w-0">
                                <SettingLabel
                                  label={t(
                                    'admin.syncWorker.dataValidationEnabled',
                                    'Enable data validation worker'
                                  )}
                                  help={t(
                                    'admin.syncWorker.dataValidationEnabledDescription',
                                    'Allows the maintenance worker to process explicitly triggered data parity validation runs.'
                                  )}
                                />
                                <FormDescription>
                                  {t(
                                    'admin.syncWorker.dataValidationEnabledDescription',
                                    'Allows the maintenance worker to process explicitly triggered data parity validation runs.'
                                  )}
                                </FormDescription>
                              </div>
                              <FormControl>
                                <Switch
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                  disabled={isReadOnly || isLoading || !workerSyncEnabled}
                                />
                              </FormControl>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid gap-4 sm:grid-cols-2">
                        <FormField
                          control={form.control}
                          name="dataValidationMaxSampleLimit"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.dataValidationMaxSampleLimit',
                                  'Max sample limit'
                                )}
                                help={t(
                                  'admin.syncWorker.dataValidationMaxSampleLimitDescription',
                                  'Largest sample size an operator can request from the UI or API without full-scan approval.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  value={field.value}
                                  onChange={(event) => {
                                    const value = Number(event.target.value);
                                    field.onChange(value);
                                    setValidationLimit((current) =>
                                      Math.min(
                                        Math.max(1, current),
                                        Number.isNaN(value) ? 1 : value
                                      )
                                    );
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="dataValidationDefaultChunkSize"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.dataValidationDefaultChunkSize',
                                  'Chunk size'
                                )}
                                help={t(
                                  'admin.syncWorker.dataValidationDefaultChunkSizeDescription',
                                  'How many local rows one worker chunk validates before yielding.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  max={dataValidationMaxSampleLimit}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(Number(event.target.value));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              <FormDescription>
                                {t('admin.syncWorker.dataValidationChunkLimitHint', {
                                  defaultValue: 'Must be <= max sample limit.',
                                })}
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="dataValidationBubbleRequestConcurrency"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.dataValidationBubbleRequestConcurrency',
                                  'Bubble concurrency'
                                )}
                                help={t(
                                  'admin.syncWorker.dataValidationBubbleRequestConcurrencyDescription',
                                  'Maximum Bubble Data API requests the validator may run concurrently.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(Number(event.target.value));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="dataValidationBubbleRequestsPerMinute"
                          render={({ field }) => (
                            <FormItem>
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.dataValidationBubbleRequestsPerMinute',
                                  'Bubble requests/min'
                                )}
                                help={t(
                                  'admin.syncWorker.dataValidationBubbleRequestsPerMinuteDescription',
                                  'Soft request budget for Bubble API calls during validation.'
                                )}
                              />
                              <FormControl>
                                <Input
                                  type="number"
                                  min={1}
                                  value={field.value}
                                  onChange={(event) => {
                                    field.onChange(Number(event.target.value));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <Alert>
                        <AlertDescription>
                          {t('admin.syncWorker.dataValidationBackendOnlyNotice', {
                            defaultValue:
                              'Backend-only: report retention, cleanup batch size, lease windows, report size cap, and full-scan enablement are intentionally not editable here.',
                          })}
                        </AlertDescription>
                      </Alert>

                      <Text size="sm" color="muted">
                        {t('admin.syncWorker.dataValidationEffectiveSummary', {
                          defaultValue:
                            'Current run budget: chunks {{chunkSize}}, Bubble concurrency {{concurrency}}, {{rpm}} requests/min.',
                          chunkSize: String(dataValidationDefaultChunkSize),
                          concurrency: String(dataValidationBubbleRequestConcurrency),
                          rpm: String(dataValidationBubbleRequestsPerMinute),
                        })}
                      </Text>
                    </Stack>
                  </RuntimeFieldCard>

                  <RuntimeFieldCard
                    className="lg:col-span-2"
                    title={t('admin.syncWorker.dataValidationRunTitle', 'Run Data Validation')}
                    description={t(
                      'admin.syncWorker.dataValidationRunDescription',
                      'Start one Bubble/Postgres parity validation run for a selected synced table. The backend rejects concurrent table runs.'
                    )}
                  >
                    <Stack gap="md">
                      <div>
                        <SettingLabel
                          label={t('admin.syncWorker.dataValidationTable', 'Table')}
                          help={t(
                            'admin.syncWorker.dataValidationTableDescription',
                            'Only synced entity tables with validation contracts are listed. home_page_log_state is intentionally excluded.'
                          )}
                        />
                        <Select
                          value={selectedValidationTable ?? ''}
                          onValueChange={(value) => {
                            onSelectedValidationTableChange?.(value);
                          }}
                          disabled={isReadOnly || isLoading || validationTableOptions.length === 0}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={t(
                                'admin.syncWorker.dataValidationTablePlaceholder',
                                'Pick table'
                              )}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {validationTableOptions.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                                disabled={!option.enabled}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                          <SettingLabel
                            label={t('admin.syncWorker.dataValidationMode', 'Mode')}
                            help={t(
                              'admin.syncWorker.dataValidationModeDescription',
                              'Sample validates a bounded number of rows. Full scan requires the backend DB gate and explicit confirmation.'
                            )}
                          />
                          <Select
                            value={validationMode}
                            onValueChange={(value) => {
                              const mode = value === 'full' ? 'full' : 'sample';
                              setValidationMode(mode);
                              if (mode === 'sample') {
                                setConfirmFullValidation(false);
                              }
                            }}
                            disabled={isReadOnly || isLoading}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sample">
                                {t('admin.syncWorker.dataValidationModeSample', 'Sample')}
                              </SelectItem>
                              <SelectItem value="full" disabled={!dataValidationFullScanEnabled}>
                                {t('admin.syncWorker.dataValidationModeFull', 'Full scan')}
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <SettingLabel
                            label={t('admin.syncWorker.dataValidationLimit', 'Limit')}
                            help={t(
                              'admin.syncWorker.dataValidationLimitDescription',
                              'Number of rows to validate in sample mode.'
                            )}
                          />
                          <Input
                            type="number"
                            min={1}
                            max={dataValidationMaxSampleLimit}
                            value={validationLimit}
                            onChange={(event) => {
                              const value = Number(event.target.value);
                              if (Number.isNaN(value)) return;
                              setValidationLimit(
                                Math.max(
                                  1,
                                  Math.min(dataValidationMaxSampleLimit, Math.trunc(value))
                                )
                              );
                            }}
                            disabled={isReadOnly || isLoading || validationMode === 'full'}
                          />
                        </div>
                      </div>

                      <div className="rounded-md border border-border p-3">
                        <Stack gap="sm">
                          <div className="flex items-center justify-between gap-4">
                            <div className="min-w-0">
                              <Text className="text-sm font-medium text-card-foreground">
                                {t(
                                  'admin.syncWorker.dataValidationScopeEnabled',
                                  'Use date window'
                                )}
                              </Text>
                              <Text size="sm" color="muted" className="mt-1">
                                {t(
                                  'admin.syncWorker.dataValidationScopeDescription',
                                  'Optional UTC [from, to) scope for this individual validation run.'
                                )}
                              </Text>
                            </div>
                            <Switch
                              checked={validationScopeEnabled}
                              onCheckedChange={setValidationScopeEnabled}
                              disabled={isReadOnly || isLoading}
                            />
                          </div>

                          {validationScopeEnabled ? (
                            <div className="grid gap-4 sm:grid-cols-2">
                              <div>
                                <SettingLabel
                                  label={t('admin.syncWorker.dataValidationScopeBasis', 'Basis')}
                                  help={t(
                                    'admin.syncWorker.dataValidationScopeBasisDescription',
                                    'Timestamp basis used by the backend for the scoped predicate.'
                                  )}
                                />
                                <Select
                                  value={validationScopeBasis}
                                  onValueChange={(value) => {
                                    if (
                                      value === 'bubble_created_at' ||
                                      value === 'bubble_modified_at' ||
                                      value === 'postgres_synced_at'
                                    ) {
                                      setValidationScopeBasis(value);
                                    }
                                  }}
                                  disabled={isReadOnly || isLoading}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="bubble_modified_at">
                                      {validationScopeBasisLabels.bubble_modified_at}
                                    </SelectItem>
                                    <SelectItem value="bubble_created_at">
                                      {validationScopeBasisLabels.bubble_created_at}
                                    </SelectItem>
                                    <SelectItem value="postgres_synced_at">
                                      {validationScopeBasisLabels.postgres_synced_at}
                                    </SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>

                              <div>
                                <SettingLabel
                                  label={t('admin.syncWorker.dataValidationScopeLabel', 'Label')}
                                  help={t(
                                    'admin.syncWorker.dataValidationScopeLabelDescription',
                                    'Evidence label stored with the run and report.'
                                  )}
                                />
                                <Input
                                  value={validationScopeLabel}
                                  onChange={(event) => {
                                    setValidationScopeLabel(event.target.value);
                                  }}
                                  disabled={isReadOnly || isLoading}
                                  placeholder="monthly_validation_window"
                                />
                              </div>

                              <div className="sm:col-span-2">
                                <SettingLabel
                                  label={t(
                                    'admin.syncWorker.dataValidationScopeRange',
                                    'Date window'
                                  )}
                                  help={t(
                                    'admin.syncWorker.dataValidationScopeRangeDescription',
                                    'Calendar days are sent as an inclusive UTC start and exclusive UTC next-day end.'
                                  )}
                                />
                                <DateRangePicker
                                  label={buildValidationScopeDateRangeLabel(
                                    validationScopeRange.from,
                                    validationScopeRange.to,
                                    i18n.language
                                  )}
                                  locale={i18n.language}
                                  minDate="2020-01-01"
                                  maxDate={toIsoDateOnly(new Date())}
                                  selectedStart={validationScopeRange.from}
                                  selectedEnd={validationScopeRange.to}
                                  quickFilters={[
                                    {
                                      id: 'yesterday',
                                      label: t(
                                        'admin.syncWorker.dataValidationScopeYesterday',
                                        'Yesterday'
                                      ),
                                    },
                                    {
                                      id: 'last_7_days',
                                      label: t(
                                        'admin.syncWorker.dataValidationScopeLast7Days',
                                        'Last 7 days'
                                      ),
                                    },
                                    {
                                      id: 'last_30_days',
                                      label: t(
                                        'admin.syncWorker.dataValidationScopeLast30Days',
                                        'Last 30 days'
                                      ),
                                    },
                                  ]}
                                  activeQuickFilterId={null}
                                  onRangeSelect={(start, end) => {
                                    if (isReadOnly || isLoading) return;
                                    setValidationScopeRange({ from: start, to: end });
                                  }}
                                  onQuickFilterSelect={(filterId) => {
                                    if (isReadOnly || isLoading) return;
                                    if (filterId === 'last_30_days') {
                                      setValidationScopeRange(getRecentValidationScopeRange(30));
                                      return;
                                    }
                                    if (filterId === 'last_7_days') {
                                      setValidationScopeRange(getRecentValidationScopeRange(7));
                                      return;
                                    }
                                    setValidationScopeRange(getDefaultValidationScopeRange());
                                  }}
                                  onClear={() => {
                                    if (isReadOnly || isLoading) return;
                                    setValidationScopeRange(getDefaultValidationScopeRange());
                                  }}
                                  onPrevious={() => {
                                    if (isReadOnly || isLoading) return;
                                    setValidationScopeRange((current) => ({
                                      from: toIsoDateOnly(
                                        new Date(
                                          (parseIsoDateOnly(current.from)?.getTime() ?? 0) -
                                            7 * 24 * 60 * 60 * 1000
                                        )
                                      ),
                                      to: toIsoDateOnly(
                                        new Date(
                                          (parseIsoDateOnly(current.to)?.getTime() ?? 0) -
                                            7 * 24 * 60 * 60 * 1000
                                        )
                                      ),
                                    }));
                                  }}
                                  onNext={() => {
                                    if (isReadOnly || isLoading) return;
                                    setValidationScopeRange((current) => ({
                                      from: toIsoDateOnly(
                                        new Date(
                                          (parseIsoDateOnly(current.from)?.getTime() ?? 0) +
                                            7 * 24 * 60 * 60 * 1000
                                        )
                                      ),
                                      to: toIsoDateOnly(
                                        new Date(
                                          (parseIsoDateOnly(current.to)?.getTime() ?? 0) +
                                            7 * 24 * 60 * 60 * 1000
                                        )
                                      ),
                                    }));
                                  }}
                                  disabled={isReadOnly || isLoading}
                                  labels={{
                                    previous: t(
                                      'admin.syncWorker.dataValidationScopePrevious',
                                      'Previous'
                                    ),
                                    next: t('admin.syncWorker.dataValidationScopeNext', 'Next'),
                                    clear: t('admin.syncWorker.dataValidationScopeReset', 'Reset'),
                                    quickFiltersTitle: t(
                                      'admin.syncWorker.dataValidationScopeQuickRanges',
                                      'Quick ranges'
                                    ),
                                    monthPickerLabel: t(
                                      'admin.syncWorker.dataValidationScopePicker',
                                      'Select validation date window'
                                    ),
                                  }}
                                  triggerClassName="min-h-11"
                                  popoverClassName="max-w-[32rem]"
                                />
                              </div>
                            </div>
                          ) : null}
                        </Stack>
                      </div>

                      {validationMode === 'full' ? (
                        <div className="rounded-md border border-border p-3">
                          <div className="flex items-center justify-between gap-4">
                            <div className="min-w-0">
                              <Text className="text-sm font-medium text-card-foreground">
                                {t(
                                  'admin.syncWorker.dataValidationConfirmFull',
                                  'Confirm full scan'
                                )}
                              </Text>
                              <Text size="sm" color="muted" className="mt-1">
                                {dataValidationFullScanEnabled
                                  ? t(
                                      'admin.syncWorker.dataValidationConfirmFullDescription',
                                      'This validates the whole selected table and can run for a long time.'
                                    )
                                  : t(
                                      'admin.syncWorker.dataValidationFullDisabled',
                                      'Full scan is disabled by backend DB settings.'
                                    )}
                              </Text>
                            </div>
                            <Switch
                              checked={confirmFullValidation}
                              onCheckedChange={setConfirmFullValidation}
                              disabled={isReadOnly || isLoading || !dataValidationFullScanEnabled}
                            />
                          </div>
                        </div>
                      ) : null}

                      <Button
                        type="button"
                        className="h-auto whitespace-normal py-2"
                        disabled={
                          isReadOnly ||
                          isLoading ||
                          isStartValidationRunPending ||
                          validationActionsDisabled ||
                          activeValidationRun !== null ||
                          !selectedValidationTable ||
                          validationScopeInvalid ||
                          (validationMode === 'full' &&
                            (!dataValidationFullScanEnabled || !confirmFullValidation))
                        }
                        onClick={() => {
                          if (!selectedValidationTable) return;
                          const scope =
                            validationScopeEnabled &&
                            validationScopeFromIso !== null &&
                            validationScopeToIso !== null
                              ? {
                                  type: 'date_window' as const,
                                  basis: validationScopeBasis,
                                  from: validationScopeFromIso,
                                  to: validationScopeToIso,
                                  ...(validationScopeLabel.trim().length === 0
                                    ? {}
                                    : { label: validationScopeLabel.trim() }),
                                }
                              : undefined;
                          void onStartValidationRun?.({
                            bubbleKey: selectedValidationTable,
                            mode: validationMode,
                            limit: validationMode === 'sample' ? validationLimit : undefined,
                            confirmFullScan:
                              validationMode === 'full' ? confirmFullValidation : undefined,
                            scope,
                            idempotencyKey: `ui-${selectedValidationTable}-${validationMode}-${String(Date.now())}`,
                          });
                        }}
                      >
                        {isStartValidationRunPending
                          ? t('admin.syncWorker.dataValidationStartLoading', 'Starting...')
                          : t('admin.syncWorker.dataValidationStart', 'Start validation')}
                      </Button>

                      {validationActionsDisabled ? (
                        <Text size="sm" color="muted">
                          {t(
                            'admin.syncWorker.dataValidationDisabledNotice',
                            'Validation actions are disabled until Worker Runtime and Data Validation are enabled.'
                          )}
                        </Text>
                      ) : null}

                      {activeValidationRun ? (
                        <div className="rounded-md border border-border p-3">
                          <Stack gap="sm">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <Text className="font-medium text-card-foreground">
                                {activeValidationRun.bubbleKey}
                              </Text>
                              <Badge appearance="outline">
                                {formatValidationRunStatus(activeValidationRun)}
                              </Badge>
                            </div>
                            <Progress value={calculateValidationProgress(activeValidationRun)} />
                            <Text size="sm" color="muted">
                              {t('admin.syncWorker.dataValidationCounters', {
                                defaultValue:
                                  '{{processed}}/{{selected}} processed, {{findings}} findings, {{warnings}} warnings, {{errors}} errors.',
                                processed: String(activeValidationRun.processedCount),
                                selected: String(activeValidationRun.selectedCount),
                                findings: String(activeValidationRun.findingCount),
                                warnings: String(activeValidationRun.warningCount),
                                errors: String(activeValidationRun.executionErrorCount),
                              })}
                            </Text>
                            {formatValidationScope(activeValidationRun) ? (
                              <Text size="sm" color="muted">
                                {formatValidationScope(activeValidationRun)}
                              </Text>
                            ) : null}
                            {renderValidationAcceptanceReadout(activeValidationRun)}
                            <Button
                              type="button"
                              variant="outline"
                              className="h-auto whitespace-normal py-2"
                              disabled={
                                isReadOnly ||
                                isLoading ||
                                isCancelValidationRunPending ||
                                activeValidationRun.cancelRequested
                              }
                              onClick={() => {
                                void onCancelValidationRun?.(activeValidationRun.id);
                              }}
                            >
                              {activeValidationRun.cancelRequested
                                ? t(
                                    'admin.syncWorker.dataValidationCancelRequested',
                                    'Cancel requested'
                                  )
                                : isCancelValidationRunPending
                                  ? t(
                                      'admin.syncWorker.dataValidationCancelLoading',
                                      'Cancelling...'
                                    )
                                  : t('admin.syncWorker.dataValidationCancel', 'Cancel run')}
                            </Button>
                          </Stack>
                        </div>
                      ) : null}
                    </Stack>
                  </RuntimeFieldCard>

                  <RuntimeFieldCard
                    className="lg:col-span-2"
                    title={t(
                      'admin.syncWorker.dataValidationRecentRunsTitle',
                      'Recent Validation Runs'
                    )}
                    description={t(
                      'admin.syncWorker.dataValidationRecentRunsDescription',
                      'Shows one backend-paginated page of data parity validation reports at a time.'
                    )}
                  >
                    <Stack gap="md">
                      {recentValidationRuns.length > 0 ? (
                        <div className="grid gap-2">
                          {recentValidationRuns.map((run) => (
                            <div
                              key={run.id}
                              className="flex flex-col gap-2 rounded-md border border-border p-3 sm:flex-row sm:items-center sm:justify-between"
                            >
                              <div className="min-w-0">
                                <Text className="text-sm font-medium text-card-foreground">
                                  {run.bubbleKey}
                                </Text>
                                <Text size="sm" color="muted">
                                  {formatValidationRunStatus(run)} -{' '}
                                  {formatValidationReportState(run)}
                                </Text>
                                {formatValidationScope(run) ? (
                                  <Text size="sm" color="muted">
                                    {formatValidationScope(run)}
                                  </Text>
                                ) : null}
                                {renderValidationAcceptanceReadout(run)}
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                className="h-auto whitespace-normal py-2"
                                disabled={
                                  isReadOnly ||
                                  isOpenValidationReportPending ||
                                  run.reportState !== 'available'
                                }
                                onClick={() => {
                                  void onOpenValidationReport?.(run.id);
                                }}
                              >
                                {t('admin.syncWorker.dataValidationOpenReport', 'Open report')}
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <Text size="sm" color="muted">
                          {t(
                            'admin.syncWorker.dataValidationRecentRunsEmpty',
                            'No validation reports on this page.'
                          )}
                        </Text>
                      )}

                      <PaginationControls
                        totalItems={validationRunsTotalItems}
                        pageSize={validationRunsPageSize}
                        currentPage={validationRunsCurrentPage}
                        onPageChange={(page) => {
                          if (page === validationRunsCurrentPage + 1) {
                            onValidationRunsNextPage?.();
                            return;
                          }
                          if (page === validationRunsCurrentPage - 1) {
                            onValidationRunsPreviousPage?.();
                            return;
                          }
                          onValidationRunsPageChange?.(page);
                        }}
                        disabled={isReadOnly || isValidationRunsFetching}
                        labels={{
                          showing: (from, to, total) =>
                            t('admin.pagination.showing', {
                              defaultValue: 'Showing {{from}}-{{to}} of {{total}}',
                              from: String(from),
                              to: String(to),
                              total: String(total),
                            }),
                          page: t('admin.pagination.pageLabel', 'Page'),
                          previous: t('admin.syncWorker.dataValidationPreviousPage', 'Previous'),
                          next: t('admin.syncWorker.dataValidationNextPage', 'Next'),
                          of: (totalPages) =>
                            t('admin.pagination.ofLabel', {
                              defaultValue: 'of {{total}}',
                              total: String(totalPages),
                            }),
                        }}
                      />
                    </Stack>
                  </RuntimeFieldCard>
                </div>
              </TabsContent>

              <TabsContent value="operator-tools" className="mt-4">
                <div className="grid gap-4 lg:grid-cols-2">
                  <RuntimeFieldCard
                    title={t('admin.syncWorker.testModeTitle', 'Validation test mode')}
                    description={t(
                      'admin.syncWorker.testModeDescription',
                      'Use bounded validation sync against one selected table and a fixed record cap.'
                    )}
                  >
                    <FormField
                      control={form.control}
                      name="testModeEnabled"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center justify-between gap-4">
                            <div className="min-w-0">
                              <SettingLabel
                                label={t('admin.syncWorker.testModeEnabled', 'Test Mode Enabled')}
                                help={t(
                                  'admin.syncWorker.testModeEnabledDescription',
                                  'When enabled, manual delta sync from this page is limited to one selected table and a bounded record count.'
                                )}
                              />
                              <FormDescription>
                                {t(
                                  'admin.syncWorker.testModeEnabledDescription',
                                  'When enabled, manual delta sync from this page is limited to one selected table and a bounded record count.'
                                )}
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={isReadOnly || isLoading}
                              />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="grid gap-4 sm:grid-cols-2">
                      <FormField
                        control={form.control}
                        name="testModeBubbleKey"
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t('admin.syncWorker.testModeDataType', 'Validation Data Type')}
                              help={t(
                                'admin.syncWorker.testModeDataTypeDescription',
                                'The only table allowed for validation sync while test mode is on.'
                              )}
                            />
                            <Select
                              value={field.value ?? ''}
                              onValueChange={(value) => {
                                field.onChange(value === '' ? null : value);
                              }}
                              disabled={isReadOnly || isLoading || !testModeEnabled}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue
                                    placeholder={t(
                                      'admin.syncWorker.testModeDataTypePlaceholder',
                                      'Select one data type'
                                    )}
                                  />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {testModeTableOptions.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="testModeRecordLimit"
                        render={({ field }) => (
                          <FormItem>
                            <SettingLabel
                              label={t(
                                'admin.syncWorker.testModeRecordLimit',
                                'Validation Record Limit'
                              )}
                              help={t(
                                'admin.syncWorker.testModeRecordLimitDescription',
                                'Maximum number of source rows fetched during validation sync.'
                              )}
                            />
                            <FormControl>
                              <Input
                                type="number"
                                min={1}
                                max={250}
                                value={field.value}
                                onChange={(event) => {
                                  field.onChange(Number(event.target.value));
                                }}
                                disabled={isReadOnly || isLoading || !testModeEnabled}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </RuntimeFieldCard>

                  <RuntimeFieldCard
                    title={t('admin.syncWorker.operatorActionsTitle', 'Operator actions')}
                    description={t(
                      'admin.syncWorker.operatorActionsDescription',
                      'Immediate actions for controlled delta runs and global pause/resume.'
                    )}
                  >
                    <div className="grid gap-3">
                      <div>
                        <SettingLabel
                          label={t('admin.syncWorker.deltaSelectedTable', 'Selected table')}
                          help={t(
                            'admin.syncWorker.deltaSelectedTableHelp',
                            'Used by the single-table delta action below.'
                          )}
                        />
                        <Select
                          value={selectedDeltaTable ?? ''}
                          onValueChange={(value) => {
                            onSelectedDeltaTableChange?.(value);
                          }}
                          disabled={isReadOnly || isLoading || deltaTableOptions.length === 0}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={t(
                                'admin.syncWorker.deltaSelectedTablePlaceholder',
                                'Pick table'
                              )}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {deltaTableOptions.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                                disabled={!option.enabled}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid gap-3 sm:grid-cols-2">
                        <Button
                          type="button"
                          variant="outline"
                          className="h-auto whitespace-normal py-2 text-left sm:text-center"
                          disabled={
                            isReadOnly ||
                            isLoading ||
                            isRunDeltaAllPending ||
                            manualDeltaActionsDisabled
                          }
                          onClick={() => {
                            void onRunDeltaAll?.();
                          }}
                        >
                          {isRunDeltaAllPending
                            ? t('admin.syncWorker.deltaAllLoading', 'Starting...')
                            : testModeEnabled
                              ? t(
                                  'admin.syncWorker.deltaAllActionTestMode',
                                  'Run Validation Sync (Configured table)'
                                )
                              : t(
                                  'admin.syncWorker.deltaAllAction',
                                  'Run Delta (All enabled tables)'
                                )}
                        </Button>

                        <Button
                          type="button"
                          variant="outline"
                          className="h-auto whitespace-normal py-2 text-left sm:text-center"
                          disabled={
                            isReadOnly ||
                            isLoading ||
                            isRunDeltaSinglePending ||
                            manualDeltaActionsDisabled ||
                            !selectedDeltaTable ||
                            (testModeEnabled && selectedDeltaTable !== testModeBubbleKey) ||
                            !deltaTableOptions.some(
                              (option) => option.value === selectedDeltaTable && option.enabled
                            )
                          }
                          onClick={() => {
                            if (!selectedDeltaTable) return;
                            void onRunDeltaSingle?.(selectedDeltaTable);
                          }}
                        >
                          {isRunDeltaSinglePending
                            ? t('admin.syncWorker.deltaSingleLoading', 'Starting...')
                            : testModeEnabled
                              ? t(
                                  'admin.syncWorker.deltaSingleActionTestMode',
                                  'Run Validation Sync (Selected table)'
                                )
                              : t(
                                  'admin.syncWorker.deltaSingleAction',
                                  'Run Delta (Selected table)'
                                )}
                        </Button>
                      </div>

                      {onToggleGlobalPause ? (
                        <Button
                          type="button"
                          variant="outline"
                          className="h-auto whitespace-normal py-2 text-left sm:text-center"
                          disabled={isReadOnly || isLoading || isGlobalPausePending}
                          onClick={() => {
                            void onToggleGlobalPause(!isGloballyPaused);
                          }}
                        >
                          {isGlobalPausePending
                            ? t('admin.syncWorker.globalControlLoading', 'Applying...')
                            : isGloballyPaused
                              ? t('admin.syncWorker.globalResume', 'Resume Sync (Global)')
                              : t('admin.syncWorker.globalPause', 'Pause Sync (Global)')}
                        </Button>
                      ) : null}
                    </div>
                  </RuntimeFieldCard>

                  <RuntimeFieldCard
                    title={t(
                      'admin.syncWorker.syncReconciliationRepairTitle',
                      'Symmetric Reconciliation Repair'
                    )}
                    description={t(
                      'admin.syncWorker.syncReconciliationRepairDescription',
                      'Master switch for scheduled reconciliation and explicit manual reconciliation runs.'
                    )}
                  >
                    <FormField
                      control={form.control}
                      name="syncSymmetricReconciliationEnabled"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center justify-between gap-4">
                            <div className="min-w-0">
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.syncSymmetricReconciliationEnabled',
                                  'Enable symmetric reconciliation'
                                )}
                                help={t(
                                  'admin.syncWorker.syncSymmetricReconciliationEnabledDescription',
                                  'Master switch for scheduled tier reconciliation and manual reconciliation runs.'
                                )}
                              />
                              <FormDescription>
                                {t(
                                  'admin.syncWorker.syncSymmetricReconciliationEnabledDescription',
                                  'Master switch for scheduled tier reconciliation and manual reconciliation runs.'
                                )}
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={isReadOnly || isLoading || !workerSyncEnabled}
                              />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </RuntimeFieldCard>

                  <RuntimeFieldCard
                    title={t(
                      'admin.syncWorker.manualReconciliationTitle',
                      'Manual reconciliation run'
                    )}
                    description={t(
                      'admin.syncWorker.manualReconciliationDescription',
                      'Start a bounded symmetric reconciliation run for one table when an operator explicitly chooses reconciliation instead of delta handoff.'
                    )}
                  >
                    {!persistedReconciliationEnabled ? (
                      <Alert>
                        <AlertDescription>
                          {t(
                            'admin.syncWorker.manualReconciliationDisabled',
                            'Enable and save Symmetric Reconciliation Repair before starting manual runs.'
                          )}
                        </AlertDescription>
                      </Alert>
                    ) : null}

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <SettingLabel
                          label={t(
                            'admin.syncWorker.manualReconciliationTable',
                            'Reconciliation table'
                          )}
                          help={t(
                            'admin.syncWorker.manualReconciliationTableHelp',
                            'One synchronized Bubble data type and its mapped Postgres table.'
                          )}
                        />
                        <Select
                          value={selectedReconciliationTable ?? ''}
                          onValueChange={(value) => {
                            onSelectedReconciliationTableChange?.(value);
                          }}
                          disabled={
                            isReadOnly || isLoading || reconciliationTableOptions.length === 0
                          }
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={t(
                                'admin.syncWorker.manualReconciliationTablePlaceholder',
                                'Pick table'
                              )}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {reconciliationTableOptions.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                                disabled={!option.enabled}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <SettingLabel
                          label={t('admin.syncWorker.manualReconciliationMode', 'Run mode')}
                          help={t(
                            'admin.syncWorker.manualReconciliationModeHelp',
                            'Dry run records evidence; write-back may hand resolved actions to exact-ID sync or delete backlog.'
                          )}
                        />
                        <Select
                          value={manualReconciliationMode}
                          onValueChange={(value) => {
                            setManualReconciliationMode(value as SyncReconciliationRunMode);
                          }}
                          disabled={isReadOnly || isLoading}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="dry_run">
                              {t('admin.syncWorker.manualReconciliationDryRun', 'Dry run')}
                            </SelectItem>
                            <SelectItem value="write_back">
                              {t('admin.syncWorker.manualReconciliationWriteBack', 'Write-back')}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <SettingLabel
                          label={t('admin.syncWorker.manualReconciliationWindowMode', 'Window')}
                          help={t(
                            'admin.syncWorker.manualReconciliationWindowModeHelp',
                            'Use a bounded date window by default. Full table is reserved for explicit operator recovery.'
                          )}
                        />
                        <Select
                          value={manualReconciliationWindowMode}
                          onValueChange={(value) => {
                            setManualReconciliationWindowMode(
                              value as ManualReconciliationWindowMode
                            );
                            if (value !== 'full_table') {
                              setManualReconciliationFullConfirmed(false);
                            }
                          }}
                          disabled={isReadOnly || isLoading}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="recent_window">
                              {t(
                                'admin.syncWorker.manualReconciliationRecentWindow',
                                'Date window'
                              )}
                            </SelectItem>
                            <SelectItem value="full_table">
                              {t('admin.syncWorker.manualReconciliationFullTable', 'Full table')}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <SettingLabel
                          label={t('admin.syncWorker.manualReconciliationReason', 'Reason')}
                          help={t(
                            'admin.syncWorker.manualReconciliationReasonHelp',
                            'Stored with the run so later diagnostics can explain why it was started.'
                          )}
                        />
                        <Textarea
                          value={manualReconciliationReason}
                          onChange={(event) => {
                            setManualReconciliationReason(event.target.value);
                          }}
                          disabled={isReadOnly || isLoading}
                          rows={3}
                        />
                      </div>
                    </div>

                    <DateRangePicker
                      label={
                        manualReconciliationWindowMode === 'full_table'
                          ? t(
                              'admin.syncWorker.manualReconciliationFullWindowLabel',
                              'Full table window'
                            )
                          : buildValidationScopeDateRangeLabel(
                              manualReconciliationRange.from,
                              manualReconciliationRange.to,
                              i18n.language
                            )
                      }
                      locale={i18n.language}
                      minDate={null}
                      maxDate={manualReconciliationDateWindowMaxDate}
                      selectedStart={manualReconciliationRange.from}
                      selectedEnd={manualReconciliationRange.to}
                      quickFilters={[
                        {
                          id: 'previous_day',
                          label: t(
                            'admin.syncWorker.manualReconciliationPreviousDay',
                            'Previous day'
                          ),
                        },
                        {
                          id: 'last_7_days',
                          label: t('admin.syncWorker.manualReconciliationLast7Days', 'Last 7 days'),
                        },
                      ]}
                      activeQuickFilterId={null}
                      showClear={false}
                      onRangeSelect={(from, to) => {
                        setManualReconciliationRange({ from, to });
                      }}
                      onQuickFilterSelect={(filterId) => {
                        setManualReconciliationRange(
                          filterId === 'last_7_days'
                            ? getRecentValidationScopeRange(7)
                            : getDefaultManualReconciliationRange()
                        );
                      }}
                      onClear={() => {
                        setManualReconciliationRange(getDefaultManualReconciliationRange());
                      }}
                      disabled={
                        isReadOnly || isLoading || manualReconciliationWindowMode === 'full_table'
                      }
                      labels={{
                        previous: t('common.previous', 'Previous'),
                        next: t('common.next', 'Next'),
                        clear: t('common.clear', 'Clear'),
                        quickFiltersTitle: t(
                          'admin.syncWorker.manualReconciliationQuickFilters',
                          'Quick windows'
                        ),
                        monthPickerLabel: t('admin.syncWorker.manualReconciliationMonth', 'Month'),
                      }}
                      triggerClassName="w-full"
                    />

                    {manualReconciliationWindowMode === 'full_table' ? (
                      <label className="flex items-start gap-3 rounded-md border border-border p-3">
                        <Checkbox
                          checked={manualReconciliationFullConfirmed}
                          onCheckedChange={(checked) => {
                            setManualReconciliationFullConfirmed(checked === true);
                          }}
                          disabled={isReadOnly || isLoading}
                        />
                        <Text size="sm" color="muted">
                          {t(
                            'admin.syncWorker.manualReconciliationFullConfirm',
                            'I understand this starts a full-table reconciliation window and should only be used for explicit operator recovery.'
                          )}
                        </Text>
                      </label>
                    ) : null}

                    <Button
                      type="button"
                      variant="outline"
                      className="h-auto whitespace-normal py-2 text-left sm:text-center"
                      disabled={
                        isReadOnly ||
                        isLoading ||
                        isStartReconciliationRunPending ||
                        manualReconciliationActionsDisabled ||
                        selectedReconciliationTarget === null ||
                        manualReconciliationWindowInvalid ||
                        manualReconciliationFullRunBlocked
                      }
                      onClick={() => {
                        if (
                          selectedReconciliationTarget === null ||
                          manualReconciliationWindowStart === null ||
                          manualReconciliationWindowEnd === null
                        ) {
                          return;
                        }

                        void onStartReconciliationRun?.({
                          bubbleKey: selectedReconciliationTarget.value,
                          postgresTableName: selectedReconciliationTarget.postgresTableName,
                          mode: manualReconciliationMode,
                          windowStart: manualReconciliationWindowStart,
                          windowEnd: manualReconciliationWindowEnd,
                          reason: manualReconciliationReason.trim() || undefined,
                        });
                      }}
                    >
                      {isStartReconciliationRunPending
                        ? t('admin.syncWorker.manualReconciliationStarting', 'Starting...')
                        : t(
                            'admin.syncWorker.manualReconciliationStart',
                            'Start manual reconciliation'
                          )}
                    </Button>
                  </RuntimeFieldCard>
                </div>
              </TabsContent>

              <TabsContent value="counts" className="mt-4">
                <RuntimeFieldCard
                  title={t('admin.syncWorker.countsRefresh', 'Inventory Counts')}
                  description={t(
                    'admin.syncWorker.countsRefreshDescription',
                    'Controls Data Types Inventory recount freshness and load without a deploy.'
                  )}
                >
                  <div className="grid gap-4 lg:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="inventoryPostgresCountEnabled"
                      render={({ field }) => (
                        <FormItem className="rounded-md border border-border p-3">
                          <div className="flex items-center justify-between gap-4">
                            <div className="min-w-0">
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.inventoryPostgresCountEnabled',
                                  'Enable PostgreSQL counting'
                                )}
                                help={t(
                                  'admin.syncWorker.inventoryPostgresCountEnabledDescription',
                                  'Schedules local PostgreSQL count refreshes using the saved database schedule.'
                                )}
                              />
                              <FormDescription>
                                {t(
                                  'admin.syncWorker.inventoryPostgresCountEnabledDescription',
                                  'Schedules local PostgreSQL count refreshes using the saved database schedule.'
                                )}
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={isReadOnly || isLoading || !workerSyncEnabled}
                              />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="inventoryPostgresCountCron"
                      render={({ field }) => (
                        <FormItem className="rounded-md border border-border p-3">
                          <SettingLabel
                            label={t(
                              'admin.syncWorker.inventoryPostgresCountCron',
                              'PostgreSQL schedule'
                            )}
                            help={t(
                              'admin.syncWorker.inventoryPostgresCountCronDescription',
                              'Cron expression persisted in the database for automatic PostgreSQL counting.'
                            )}
                          />
                          <FormControl>
                            <Input
                              type="text"
                              value={field.value}
                              onChange={(event) => {
                                field.onChange(event.target.value);
                              }}
                              disabled={isReadOnly || isLoading}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="mt-4 grid gap-4 lg:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="inventoryBubbleCountEnabled"
                      render={({ field }) => (
                        <FormItem className="rounded-md border border-border p-3">
                          <div className="flex items-center justify-between gap-4">
                            <div className="min-w-0">
                              <SettingLabel
                                label={t(
                                  'admin.syncWorker.inventoryBubbleCountEnabled',
                                  'Enable Bubble counting'
                                )}
                                help={t(
                                  'admin.syncWorker.inventoryBubbleCountEnabledDescription',
                                  'Schedules Bubble count refreshes using the saved database schedule.'
                                )}
                              />
                              <FormDescription>
                                {t(
                                  'admin.syncWorker.inventoryBubbleCountEnabledDescription',
                                  'Schedules Bubble count refreshes using the saved database schedule.'
                                )}
                              </FormDescription>
                            </div>
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={isReadOnly || isLoading || !workerSyncEnabled}
                              />
                            </FormControl>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="inventoryBubbleCountCron"
                      render={({ field }) => (
                        <FormItem className="rounded-md border border-border p-3">
                          <SettingLabel
                            label={t(
                              'admin.syncWorker.inventoryBubbleCountCron',
                              'Bubble schedule'
                            )}
                            help={t(
                              'admin.syncWorker.inventoryBubbleCountCronDescription',
                              'Cron expression persisted in the database for automatic Bubble counting.'
                            )}
                          />
                          <FormControl>
                            <Input
                              type="text"
                              value={field.value}
                              onChange={(event) => {
                                field.onChange(event.target.value);
                              }}
                              disabled={isReadOnly || isLoading}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </RuntimeFieldCard>
              </TabsContent>
            </Tabs>

            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <Button
                type="submit"
                disabled={isReadOnly || isLoading || isSaving || hasUnsupportedIntervals}
              >
                {isSaving
                  ? t('admin.syncWorker.saving', 'Saving...')
                  : t('admin.syncWorker.save', 'Save')}
              </Button>
            </div>

            {!workerSyncEnabled ? (
              <Text size="sm" color="muted" className="mt-4">
                {t(
                  'admin.syncWorker.workerDisabledNotice',
                  'Worker Sync Enabled is currently off, so worker-owned scheduled jobs do not run even if schedules are configured below.'
                )}
              </Text>
            ) : null}
          </CardContent>
        </Card>
      </form>
    </Form>
  );
}

export type { SyncWorkerSettingsFormValues };
